import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, TrendingUp, BarChart3, RefreshCw, Download, Trophy, Calendar, X, ExternalLink, AlertCircle, ChevronUp, ChevronDown, Clock } from 'lucide-react';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import { getAllowedMarkets, isSymbolAllowed } from '../services/api';
import useAvailableMarkets from '../hooks/useAvailableMarkets';
import useStickyState from '../hooks/useStickyState';
import usePositionMode from '../hooks/usePositionMode';
import PositionModeToggle from '../components/PositionModeToggle';
import { DirectionBadge, ExitReasonPill } from '../components/DirectionBadge';
import * as XLSX from 'xlsx';

// Helper function to format a single date to DD-MM-YYYY
const formatSingleDate = (dateStr) => {
  if (!dateStr) return '';

  const trimmed = dateStr.trim();

  // If already in DD-MM-YYYY format, return as is
  if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
    return trimmed;
  }

  try {
    // Parse the date string
    const date = new Date(trimmed);
    if (isNaN(date.getTime())) return trimmed; // Return original if invalid

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  } catch {
    return trimmed; // Return original if parsing fails
  }
};

// Helper function to format dates to DD-MM-YYYY (handles date ranges with →)
const formatDate = (dateStr) => {
  if (!dateStr) return '';

  // Handle date ranges like "2009-03-17 → 2009-07-07"
  if (dateStr.includes('→')) {
    const parts = dateStr.split('→');
    return parts.map(part => formatSingleDate(part.trim())).join(' → ');
  }

  // Handle date ranges with " to " separator
  if (dateStr.includes(' to ')) {
    const parts = dateStr.split(' to ');
    return parts.map(part => formatSingleDate(part.trim())).join(' to ');
  }

  return formatSingleDate(dateStr);
};

const Reports = () => {
  // URL is the source of truth for which top-level view is active so refresh /
  // bookmark / shared link all land on the right tab.
  // /reports                  → individual (default)
  // /reports/individual-indexes
  // /reports/all-indexes
  const { viewMode: urlViewMode } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const URL_TO_VIEW = { 'individual-indexes': 'individual', 'all-indexes': 'all_indexes' };
  const VIEW_TO_URL = { individual: 'individual-indexes', all_indexes: 'all-indexes' };

  const viewMode = URL_TO_VIEW[urlViewMode] || 'individual';
  const setViewMode = (next) => {
    navigate(`/reports/${VIEW_TO_URL[next] || 'individual-indexes'}`);
  };

  // Sub-tab is also URL-driven via ?tab=... so it survives a refresh.
  // Defaults differ per view, hence the per-view fallback.
  const defaultSubTab = viewMode === 'all_indexes' ? 'performance_summary' : 'yearly_statistics';
  const activeTab = searchParams.get('tab') || defaultSubTab;
  const setActiveTab = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    market: 'india',
    symbol: '',
    symbols: [], // For multi-select comparison in yearly_statistics
    displayStartYear: null,  // For frontend filtering only
    displayEndYear: null,     // For frontend filtering only
    performanceFilter: 'critical', // 'all', 'best', 'worst', 'critical', 'custom' - default to critical
    fromYear: null,  // Custom year range start
    toYear: null,    // Custom year range end
    withShorts: false, // Toggle for including shorts
    compareMode: false, // Toggle for multi-index comparison
    strategy: '1x', // '1x', 'aes_moderate', or 'aes_aggressive' - strategy type for performance data
    // Transaction filters (persisted across tab switches)
    transactionFilterType: 'all', // 'all', 'losses', 'consecutive_losses', 'consecutive_gains', 'top10losses', 'top10profits', 'with_leverage'
    transactionYear: '',
    transactionStartDate: '',
    transactionEndDate: ''
  });

  // Shared positions toggle (Long Only / With Shorts / With Rec. Shorts).
  // Gated to apalshah; everyone else gets long_only and the toggle is hidden.
  const { mode: indivPositionMode, canUseShorts: indivCanUseShorts } = usePositionMode();

  // "Apply practical costs" toggle. Sticky across pages and refresh so the
  // user doesn't have to re-enable it after every navigation.
  const [netCosts, setNetCosts] = useStickyState('reports.netCosts', false);

  const [comparisonData, setComparisonData] = useState({}); // Store data for multiple symbols

  // Yearly transactions modal state
  const [yearlyTransactionsModal, setYearlyTransactionsModal] = useState({
    isOpen: false,
    year: null,
    symbol: null,
    data: null,
    loading: false,
    error: null
  });

  const [availableSymbols, setAvailableSymbols] = useState([]);
  const [allMarketSymbols, setAllMarketSymbols] = useState({}); // For cross-market comparison

  // Get allowed markets for current user
  const allowedMarkets = getAllowedMarkets();

  // Market options — fetched from backend (respects is_active + user permissions)
  const { marketOptions } = useAvailableMarkets();

  const reportTabs = [
    { id: 'yearly_statistics', label: 'Yearly Statistics', icon: BarChart3, description: 'Year-by-year performance breakdown' },
    { id: 'performance_summary', label: 'Performance Summary', icon: TrendingUp, description: 'CAGR, cumulative returns & overall metrics' },
    { id: 'all_transactions', label: 'All Transactions', icon: FileText, description: 'Complete trade history' }
  ];

  // Load available symbols when market changes
  useEffect(() => {
    fetchAvailableSymbols();
  }, [filters.market]);

  // Auto-load report when symbol, tab, position mode, or net-costs toggle
  // changes. The net-costs flag has to be a dep because it changes the
  // backend query and the user expects to see the new numbers immediately
  // without hitting Reload.
  useEffect(() => {
    if (filters.compareMode && filters.symbols.length > 0 && activeTab === 'yearly_statistics') {
      generateComparisonReport();
    } else if (filters.symbol) {
      generateReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.symbol, filters.symbols, filters.compareMode, activeTab, indivPositionMode, netCosts]);

  const fetchAvailableSymbols = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/analytics/indexes/?market=${filters.market}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.available_symbols) {
        // Filter symbols based on user's allowed symbols for this market
        const filteredSymbols = data.available_symbols.filter(symbol =>
          isSymbolAllowed(filters.market, symbol.symbol_code)
        );
        setAvailableSymbols(filteredSymbols);
        if (filteredSymbols.length > 0 && !filters.symbol) {
          setFilters(prev => ({ ...prev, symbol: filteredSymbols[0].symbol_code }));
        }
      }
    } catch (error) {
      console.error('Error fetching symbols:', error);
      setError('Failed to load available symbols');
    }
  };

  // Fetch symbols from all markets for cross-market comparison
  const fetchAllMarketSymbols = async () => {
    const markets = ['india', 'us', 'crypto', 'precious_metals', 'asia_pacific', 'europe'];
    const allowedMarketsList = allowedMarkets.includes('all') ? markets : markets.filter(m => allowedMarkets.includes(m));

    const symbolsByMarket = {};

    for (const market of allowedMarketsList) {
      try {
        const response = await fetch(`${API_BASE_URL}/analytics/indexes/?market=${market}`);
        if (response.ok) {
          const data = await response.json();
          if (data.available_symbols) {
            // Filter symbols based on user's allowed symbols for this market
            const filteredSymbols = data.available_symbols.filter(symbol =>
              isSymbolAllowed(market, symbol.symbol_code)
            );
            if (filteredSymbols.length > 0) {
              symbolsByMarket[market] = filteredSymbols;
            }
          }
        }
      } catch (error) {
        console.error(`Error fetching symbols for ${market}:`, error);
      }
    }

    setAllMarketSymbols(symbolsByMarket);
  };

  // Fetch all market symbols when compare mode is enabled
  useEffect(() => {
    if (filters.compareMode && Object.keys(allMarketSymbols).length === 0) {
      fetchAllMarketSymbols();
    }
  }, [filters.compareMode]);

  const generateReport = async () => {
    if (!filters.symbol) {
      setError('Please select a symbol');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        symbol: filters.symbol,
        position_mode: indivPositionMode,
        // Back-compat for endpoints not yet switched over to position_mode.
        with_shorts: (indivPositionMode !== 'long_only').toString(),
        strategy: '1x',
      });
      if (netCosts) queryParams.set('net_costs', 'true');

      // Determine which endpoint to call based on active tab
      let endpoint;
      switch (activeTab) {
        case 'yearly_statistics':
          endpoint = 'yearly-gross-returns';
          break;
        case 'performance_summary':
          endpoint = 'performance-summary';
          break;
        case 'all_transactions':
          endpoint = 'all-transactions';
          break;
        default:
          endpoint = 'yearly-gross-returns';
      }

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/analytics/reports/${endpoint}/?${queryParams}&t=${Date.now()}`, {
        cache: 'no-store',
        headers: token ? { 'Authorization': `Basic ${token}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const reportData = await response.json();
      setData(reportData);

      // Always reset year range to full data range when loading new report
      if (reportData.yearly_returns && reportData.yearly_returns.length > 0) {
        setFilters(prev => ({
          ...prev,
          displayStartYear: reportData.yearly_returns[0].year,
          displayEndYear: reportData.yearly_returns[reportData.yearly_returns.length - 1].year
        }));
      }
    } catch (error) {
      console.error('Error loading report:', error);
      setError(error.message || 'Failed to load report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const generateComparisonReport = async () => {
    if (filters.symbols.length === 0) {
      setError('Please select at least one symbol');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch data for all selected symbols in parallel
      const promises = filters.symbols.map(async (symbolCode) => {
        const queryParams = new URLSearchParams({
          symbol: symbolCode,
          position_mode: indivPositionMode,
          with_shorts: (indivPositionMode !== 'long_only').toString(),
          strategy: '1x',
        });
        if (netCosts) queryParams.set('net_costs', 'true');

        const compareToken = localStorage.getItem('adminToken');
        const response = await fetch(`${API_BASE_URL}/analytics/reports/yearly-gross-returns/?${queryParams}&t=${Date.now()}`, {
          cache: 'no-store',
          headers: compareToken ? { 'Authorization': `Basic ${compareToken}` } : {},
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch data for ${symbolCode}`);
        }

        return response.json();
      });

      const results = await Promise.all(promises);

      // Build comparison data object keyed by symbol
      const newComparisonData = {};
      filters.symbols.forEach((symbolCode, index) => {
        newComparisonData[symbolCode] = results[index];
      });

      setComparisonData(newComparisonData);
    } catch (error) {
      console.error('Error loading comparison report:', error);
      setError(error.message || 'Failed to load comparison report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      if (key === 'market') {
        newFilters.symbol = '';
        newFilters.symbols = [];
        setData(null);
        setComparisonData({});
      }
      if (key === 'compareMode') {
        // Clear data when switching modes
        if (value) {
          setData(null);
          // Default to 'all' years for multi-compare mode
          newFilters.performanceFilter = 'all';
        } else {
          setComparisonData({});
        }
      }
      return newFilters;
    });
  };

  const handleSymbolToggle = (symbolCode) => {
    setFilters(prev => {
      const newSymbols = prev.symbols.includes(symbolCode)
        ? prev.symbols.filter(s => s !== symbolCode)
        : [...prev.symbols, symbolCode];
      return { ...prev, symbols: newSymbols };
    });
  };

  // Handler for clicking on a year to view transactions
  // Symbol param is optional - used in comparison mode where each index has different symbol
  const handleYearClick = async (year, symbolOverride = null) => {
    const symbol = symbolOverride || filters.symbol;
    if (!symbol) return;

    setYearlyTransactionsModal({
      isOpen: true,
      year,
      symbol,
      data: null,
      loading: true,
      error: null
    });

    try {
      const yrToken = localStorage.getItem('adminToken');
      const yrUrl = `${API_BASE_URL}/analytics/reports/yearly-transactions/?symbol=${symbol}&year=${year}&strategy=${filters.strategy}&position_mode=${indivPositionMode}${netCosts ? '&net_costs=true' : ''}`;
      const response = await fetch(yrUrl, {
        headers: yrToken ? { 'Authorization': `Basic ${yrToken}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load transactions');
      }

      const transactionsData = await response.json();
      setYearlyTransactionsModal(prev => ({
        ...prev,
        data: transactionsData,
        loading: false
      }));
    } catch (err) {
      setYearlyTransactionsModal(prev => ({
        ...prev,
        loading: false,
        error: err.message
      }));
    }
  };

  const closeYearlyTransactionsModal = () => {
    setYearlyTransactionsModal({
      isOpen: false,
      year: null,
      symbol: null,
      data: null,
      loading: false,
      error: null
    });
  };

  return (
    <Layout>
      <div className="space-y-3">
        {/* Page Header - Ultra Compact */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <FileText className="w-5 h-5 text-primary-400" />
            <div>
              <h1 className="text-xl font-bold text-white">Performance Reports</h1>
            </div>
          </div>
        </div>

        {/* Top-Level Navigation: Individual vs All Indexes */}
        <div className="flex items-center space-x-1 bg-dark-800/80 border border-dark-700 p-1 rounded-lg w-fit">
          <button
            onClick={() => setViewMode('individual')}
            className={`px-6 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
              viewMode === 'individual'
                ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-lg shadow-violet-900/50'
                : 'text-dark-300 hover:text-white hover:bg-dark-700/50'
            }`}
          >
            Individual Indexes
          </button>
          <button
            onClick={() => setViewMode('all_indexes')}
            className={`px-6 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
              viewMode === 'all_indexes'
                ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-lg shadow-emerald-900/50'
                : 'text-dark-300 hover:text-white hover:bg-dark-700/50'
            }`}
          >
            All Indexes
          </button>
        </div>

        {/* Individual Indexes View */}
        {viewMode === 'individual' && (
          <div className="dashboard-card">
          <div className="flex items-center space-x-1 border-b border-dark-700 pb-0 mb-3">
            {reportTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-1.5 px-3 py-2 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-white'
                      : 'border-transparent text-dark-400 hover:text-dark-200'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span className="font-medium text-sm">{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Common Filters - Visible across all tabs */}
          <div className="space-y-3">
            {/* Row 1: Main Controls */}
            <div className="flex items-center gap-6">
              {/* Market Dropdown */}
              <div>
                <label className="block text-xs font-semibold text-dark-400 uppercase tracking-wide mb-1.5">Market</label>
                <select
                  value={filters.market}
                  onChange={(e) => handleFilterChange('market', e.target.value)}
                  className="w-44 bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {marketOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.icon} {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Symbol Dropdown - Only show when NOT in compare mode */}
              {!(filters.compareMode && activeTab === 'yearly_statistics') && (
                <div>
                  <label className="block text-xs font-semibold text-dark-400 uppercase tracking-wide mb-1.5">Symbol</label>
                  <select
                    value={filters.symbol}
                    onChange={(e) => handleFilterChange('symbol', e.target.value)}
                    className="w-44 bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={!availableSymbols.length}
                  >
                    <option value="">Select Symbol</option>
                    {availableSymbols.map(symbol => (
                      <option key={symbol.symbol_code} value={symbol.symbol_code}>
                        {symbol.symbol_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Strategy toggle — shared component, gated to apalshah, sticky.
                  Hidden for everyone else (always renders as long-only data). */}
              {indivCanUseShorts && <div className="h-10 w-px bg-dark-600"></div>}
              <PositionModeToggle />

              {/* Practical-cost toggle — applies impact + carry + interest
                  per trade and re-derives CAGR / win-rate / per-row gain.
                  Reload happens automatically via useEffect deps. */}
              <div className="h-10 w-px bg-dark-600"></div>
              <label
                className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg hover:bg-dark-700 transition-colors"
                title={
                  'Apply realistic trading frictions per symbol:\n' +
                  '  Indian (NIFTY50 0.30%, SENSEX 0.30%, NIFTYBANK 0.40%): carry 1%/mo, interest 18% on 20% margin.\n' +
                  '  US (IXIC / SNP / DJI / RUT): impact 0.10%, carry 0.5%/mo, interest 18% on 10% margin.\n' +
                  'Carry is paid by longs, earned by shorts. Affects CAGR, per-trade gain, and win rate. ' +
                  'Index CAGR stays gross. Applied to 1X book only.'
                }
              >
                <input
                  type="checkbox"
                  checked={netCosts}
                  onChange={(e) => setNetCosts(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="relative w-9 h-5 rounded-full transition-colors bg-dark-600 peer-checked:bg-amber-500">
                  <span
                    className={`absolute top-[2px] h-[16px] w-[16px] rounded-full bg-white transition-transform shadow ${
                      netCosts ? 'left-[20px]' : 'left-[2px]'
                    }`}
                  />
                </div>
                <span className={`text-xs font-semibold uppercase tracking-wide ${netCosts ? 'text-amber-300' : 'text-dark-400'}`}>
                  After Costs
                </span>
              </label>

              {/* Spacer */}
              <div className="flex-1"></div>

              {/* Reload Button */}
              <button
                onClick={() => (filters.compareMode && activeTab === 'yearly_statistics') ? generateComparisonReport() : generateReport()}
                disabled={loading || ((filters.compareMode && activeTab === 'yearly_statistics') ? filters.symbols.length === 0 : !filters.symbol)}
                className="flex items-center space-x-2 px-5 py-2 bg-primary-600 hover:bg-primary-700 disabled:bg-dark-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="text-sm">{loading ? 'Loading...' : 'Reload'}</span>
              </button>
            </div>

            {/* Row 2: Tab-specific filters - Only for Yearly Statistics */}
            {activeTab === 'yearly_statistics' && (
              <div className="flex items-end gap-4 pt-2 border-t border-dark-700/50">
                {/* Compare Mode Toggle */}
                <div className="flex items-center space-x-3 pb-1">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.compareMode}
                      onChange={(e) => handleFilterChange('compareMode', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-dark-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                    <span className="ml-3 text-sm font-medium text-white">Multi-Compare</span>
                  </label>
                </div>

                {/* Performance Filter - Hide in multi-compare mode (always All Years) */}
                {!(filters.compareMode && activeTab === 'yearly_statistics') && (
                  <div className="w-40">
                    <label className="block text-sm font-medium text-dark-400 mb-2">Performance</label>
                    <select
                      value={filters.performanceFilter}
                      onChange={(e) => handleFilterChange('performanceFilter', e.target.value)}
                      className="w-full bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">All Years</option>
                      <option value="best">Best Performers</option>
                      <option value="worst">Worst Performers</option>
                      <option value="custom">Custom Range</option>
                      <option value="critical">Critical Years</option>
                    </select>
                  </div>
                )}

                {/* Year Range - Only show for Custom performance filter */}
                {filters.performanceFilter === 'custom' && (
                  <>
                    <div className="w-24">
                      <label className="block text-sm font-medium text-dark-400 mb-2">From</label>
                      <input
                        type="number"
                        min="1990"
                        max="2030"
                        value={filters.fromYear || ''}
                        onChange={(e) => handleFilterChange('fromYear', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="2010"
                        className="w-full bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div className="w-24">
                      <label className="block text-sm font-medium text-dark-400 mb-2">To</label>
                      <input
                        type="number"
                        min="1990"
                        max="2030"
                        value={filters.toYear || ''}
                        onChange={(e) => handleFilterChange('toYear', e.target.value ? parseInt(e.target.value) : null)}
                        placeholder="2020"
                        className="w-full bg-dark-700 border border-dark-600 text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Row 3: Compact Multi-select Index Buttons - Only when Compare mode is ON */}
            {filters.compareMode && activeTab === 'yearly_statistics' && (
              <div className="bg-dark-800/50 rounded-lg p-4 border border-dark-700">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-white">
                    Add indexes ({filters.symbols.length}/4 selected)
                  </span>
                  <div className="flex items-center gap-3">
                    {filters.symbols.length >= 4 && (
                      <span className="text-xs text-amber-400">Max 4 indexes</span>
                    )}
                    {filters.symbols.length > 0 && (
                      <button
                        onClick={() => setFilters(prev => ({ ...prev, symbols: [] }))}
                        className="text-xs text-dark-400 hover:text-red-400 transition-colors"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>
                {Object.keys(allMarketSymbols).length === 0 ? (
                  <div className="text-sm text-dark-400">Loading symbols from all markets...</div>
                ) : (
                  <div className="flex flex-wrap gap-x-6 gap-y-2">
                    {/* All markets inline with market labels */}
                    {[
                      { key: 'india', label: 'India', icon: '🇮🇳' },
                      { key: 'us', label: 'US', icon: '🇺🇸' },
                      { key: 'asia_pacific', label: 'Asia', icon: '🌏' },
                      { key: 'europe', label: 'EU', icon: '🇪🇺' },
                      { key: 'crypto', label: 'Crypto', icon: '₿' },
                      { key: 'precious_metals', label: 'Metals', icon: '🥇' },
                    ].map(market => (
                      allMarketSymbols[market.key] && allMarketSymbols[market.key].length > 0 && (
                        <div key={market.key} className="flex items-center gap-1.5">
                          <span className="text-xs text-dark-500 mr-1">{market.icon}</span>
                          {allMarketSymbols[market.key].map(symbol => {
                            const isSelected = filters.symbols.includes(symbol.symbol_code);
                            const isDisabled = !isSelected && filters.symbols.length >= 4;
                            return (
                              <button
                                key={symbol.symbol_code}
                                onClick={() => !isDisabled && handleSymbolToggle(symbol.symbol_code)}
                                disabled={isDisabled}
                                className={`px-2 py-0.5 rounded text-xs font-medium transition-all ${
                                  isSelected
                                    ? 'bg-teal-600 text-white'
                                    : isDisabled
                                    ? 'bg-dark-800 text-dark-600 cursor-not-allowed'
                                    : 'bg-dark-700 text-dark-300 hover:bg-dark-600 hover:text-white'
                                }`}
                              >
                                {symbol.symbol_name}
                                {isSelected && <span className="ml-1">✓</span>}
                              </button>
                            );
                          })}
                        </div>
                      )
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Source File Info & Strategy Mode - Separate Row */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              {data && data.source_file && (
                <div className="flex items-center space-x-2 text-sm text-dark-400 bg-dark-800/50 rounded-lg px-3 py-2">
                  <span>📄</span>
                  <span>Source: {data.source_file}</span>
                  {data.last_updated && (
                    <>
                      <span className="text-dark-600">|</span>
                      <span>Updated: {(() => {
                        try {
                          const d = new Date(data.last_updated);
                          if (isNaN(d.getTime())) return data.last_updated;
                          return d.toLocaleString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
                        } catch { return data.last_updated; }
                      })()}</span>
                    </>
                  )}
                </div>
              )}
              {/* Heads-up when the user is on a shorts-capable Strategy but the
                  loaded file currently contains zero short rows — i.e. the
                  upstream pipeline dropped a long-only copy here (everything
                  except BTC/ETH today). Without this banner the page silently
                  shows long trades only with no indication why. */}
              {indivPositionMode !== 'long_only' && data?.transactions
                && data.transactions.length > 0
                && !data.transactions.some(t => t.direction === 'short') && (
                <div className="flex items-center space-x-2 text-sm bg-amber-900/20 border border-amber-700/40 text-amber-300 rounded-lg px-3 py-2">
                  <span aria-hidden="true">⚠</span>
                  <span>
                    No ML-recommended shorts have been generated for this index yet —
                    showing long-only trades.
                  </span>
                </div>
              )}
              {/* Strategy Mode indicators - HIDDEN: Shorts pending implementation */}
              {/* {filters.withShorts && (
                <div className="flex items-center space-x-2 text-sm bg-primary-900/30 border border-primary-700/50 text-primary-300 rounded-lg px-3 py-2">
                  <span className="font-semibold">Strategy Mode:</span>
                  <span>Long + Short Positions</span>
                </div>
              )}
              {!filters.withShorts && data && (
                <div className="flex items-center space-x-2 text-sm bg-blue-900/30 border border-blue-700/50 text-blue-300 rounded-lg px-3 py-2">
                  <span className="font-semibold">Strategy Mode:</span>
                  <span>Long Positions Only</span>
                </div>
              )} */}
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-3 bg-red-900/20 border border-red-800/50 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Report Content with Loading Overlay */}
          <div className="mt-6 relative">
            {/* Loading Overlay */}
            {loading && (
              <div className="absolute inset-0 bg-dark-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
                <div className="flex items-center space-x-3 bg-dark-800 px-4 py-3 rounded-lg border border-dark-600 shadow-lg">
                  <RefreshCw className="w-5 h-5 text-primary-400 animate-spin" />
                  <span className="text-sm text-white font-medium">Refreshing...</span>
                </div>
              </div>
            )}

            {/* Report Content */}
            <div className={`transition-opacity duration-300 ${loading ? 'opacity-50' : 'opacity-100'}`}>
              {activeTab === 'yearly_statistics' && filters.compareMode && Object.keys(comparisonData).length > 0 && (
                <YearlyComparisonReport
                  comparisonData={comparisonData}
                  filters={filters}
                  allMarketSymbols={allMarketSymbols}
                  onRemoveSymbol={handleSymbolToggle}
                />
              )}

              {activeTab === 'yearly_statistics' && !filters.compareMode && data && (
                <YearlyStatisticsReport data={data} filters={filters} onYearClick={handleYearClick} />
              )}

              {activeTab === 'performance_summary' && data && (
                <PerformanceSummaryReport data={data} strategy={filters.strategy} symbol={filters.symbol} />
              )}

              {activeTab === 'all_transactions' && data && (
                <AllTransactionsReport data={data} filters={filters} onFilterChange={handleFilterChange} />
              )}
            </div>
          </div>

          {/* Empty State */}
          {!loading && !error && (
            (filters.compareMode && activeTab === 'yearly_statistics' && Object.keys(comparisonData).length === 0) ||
            (!filters.compareMode && !data) ||
            (filters.compareMode && activeTab !== 'yearly_statistics' && !data)
          ) && (
            <div className="text-center py-12">
              <BarChart3 className="w-16 h-16 text-dark-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                {filters.compareMode && activeTab === 'yearly_statistics' ? 'No Comparison Data' : 'No Report Generated'}
              </h3>
              <p className="text-dark-400">
                {filters.compareMode && activeTab === 'yearly_statistics'
                  ? 'Select indexes to compare and click "Reload" to view comparison'
                  : 'Select your parameters and click "Reload" to view results'}
              </p>
            </div>
          )}
          </div>
        )}

        {/* All Indexes View */}
        {viewMode === 'all_indexes' && (
          <AllIndexesComparison />
        )}
      </div>

      {/* Yearly Transactions Modal */}
      {yearlyTransactionsModal.isOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-dark-800 rounded-xl border border-dark-600 shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-dark-700">
              <div>
                <h2 className="text-xl font-bold text-white">
                  {yearlyTransactionsModal.symbol} - {yearlyTransactionsModal.year} Transactions
                </h2>
                {yearlyTransactionsModal.data?.source_file && (
                  <p className="text-sm text-dark-400 mt-1">
                    Source: {yearlyTransactionsModal.data.source_file}
                    {yearlyTransactionsModal.data.last_updated && (
                      <span className="text-dark-500 ml-2">| Updated: {yearlyTransactionsModal.data.last_updated}</span>
                    )}
                  </p>
                )}
              </div>
              <button
                onClick={closeYearlyTransactionsModal}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-dark-400 hover:text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-auto p-6">
              {yearlyTransactionsModal.loading && (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-primary-400 animate-spin" />
                  <span className="ml-3 text-white">Loading transactions...</span>
                </div>
              )}

              {yearlyTransactionsModal.error && (
                <div className="p-4 bg-red-900/20 border border-red-800/50 rounded-lg">
                  <p className="text-red-400">{yearlyTransactionsModal.error}</p>
                </div>
              )}

              {yearlyTransactionsModal.data && !yearlyTransactionsModal.loading && (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  {yearlyTransactionsModal.data.summary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-dark-700/50 rounded-lg p-4 border border-dark-600">
                        <div className="text-sm text-dark-400 mb-1">Strategy Return</div>
                        <div className={`text-xl font-bold ${
                          yearlyTransactionsModal.data.summary.strategy_return >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {yearlyTransactionsModal.data.summary.strategy_return >= 0 ? '+' : ''}
                          {yearlyTransactionsModal.data.summary.strategy_return?.toFixed(2)}%
                        </div>
                      </div>
                      <div className="bg-dark-700/50 rounded-lg p-4 border border-dark-600">
                        <div className="text-sm text-dark-400 mb-1">Index Return</div>
                        {yearlyTransactionsModal.data.summary.index_return == null ? (
                          <div className="text-xl font-bold text-dark-400">—</div>
                        ) : (
                          <div className={`text-xl font-bold ${
                            yearlyTransactionsModal.data.summary.index_return >= 0 ? 'text-blue-300' : 'text-blue-400'
                          }`}>
                            {yearlyTransactionsModal.data.summary.index_return >= 0 ? '+' : ''}
                            {yearlyTransactionsModal.data.summary.index_return?.toFixed(2)}%
                          </div>
                        )}
                      </div>
                      <div className="bg-dark-700/50 rounded-lg p-4 border border-dark-600">
                        <div className="text-sm text-dark-400 mb-1">Win Rate</div>
                        <div className={`text-xl font-bold ${
                          parseFloat(yearlyTransactionsModal.data.summary.win_rate) >= 50 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {yearlyTransactionsModal.data.summary.win_rate}%
                        </div>
                        <div className="text-xs text-dark-400 mt-1">
                          {yearlyTransactionsModal.data.summary.wins}W / {yearlyTransactionsModal.data.summary.losses}L
                        </div>
                      </div>
                      <div className="bg-dark-700/50 rounded-lg p-4 border border-dark-600">
                        <div className="text-sm text-dark-400 mb-1">Outperformance</div>
                        {!yearlyTransactionsModal.data.summary.outperformance ? (
                          <div className="text-xl font-bold text-dark-400">—</div>
                        ) : (
                          <div className={`text-xl font-bold ${
                            yearlyTransactionsModal.data.summary.outperformance.startsWith('+') ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {yearlyTransactionsModal.data.summary.outperformance}%
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Additional Stats Row */}
                  {yearlyTransactionsModal.data.summary && (
                    <div className="grid grid-cols-4 gap-4">
                      <div className="bg-dark-700/30 rounded-lg p-3 border border-dark-700">
                        <div className="text-xs text-dark-400">Avg Gain</div>
                        <div className="text-green-400 font-semibold">
                          +{yearlyTransactionsModal.data.summary.avg_gain?.toFixed(2)}%
                        </div>
                      </div>
                      <div className="bg-dark-700/30 rounded-lg p-3 border border-dark-700">
                        <div className="text-xs text-dark-400">Avg Loss</div>
                        <div className="text-red-400 font-semibold">
                          {yearlyTransactionsModal.data.summary.avg_loss?.toFixed(2)}%
                        </div>
                      </div>
                      <div className="bg-dark-700/30 rounded-lg p-3 border border-dark-700">
                        <div className="text-xs text-dark-400">Max Gain</div>
                        <div className="text-emerald-400 font-semibold">
                          +{yearlyTransactionsModal.data.summary.max_gain?.toFixed(2)}%
                        </div>
                      </div>
                      <div className="bg-dark-700/30 rounded-lg p-3 border border-dark-700">
                        <div className="text-xs text-dark-400">Max Loss</div>
                        <div className="text-rose-400 font-semibold">
                          {yearlyTransactionsModal.data.summary.max_loss?.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Transactions Table */}
                  <div className="bg-dark-700/30 rounded-lg border border-dark-700 overflow-hidden">
                    <div className="px-4 py-3 border-b border-dark-700">
                      <h4 className="text-sm font-semibold text-white">
                        Trades ({Math.floor((yearlyTransactionsModal.data.transactions?.length || 0) / 2)})
                      </h4>
                    </div>
                    {(() => {
                      const isAesStrategy = filters.strategy === 'aes_moderate' || filters.strategy === 'aes_aggressive';
                      const transactions = yearlyTransactionsModal.data.transactions || [];
                      const hasMAE = transactions.some(tx => tx.mae !== null && tx.mae !== undefined);

                      // Create paired transactions: BUY+SELL form a long pair, SHORT SELL+SHORT COVER
                      // form a short pair. (Yearly transactions share the same trade number across the
                      // entry/exit, unlike All Transactions which use sequential numbering.)
                      const pairedTxs = [];
                      const ENTRY_TYPES = new Set(['BUY', 'SHORT SELL']);
                      const sortedTxs = [...transactions].sort((a, b) => a.trade_number - b.trade_number);
                      for (let i = 0; i < sortedTxs.length; i++) {
                        const current = sortedTxs[i];
                        if (!ENTRY_TYPES.has(current.type)) continue;
                        const isShort = current.type === 'SHORT SELL';
                        const matchingExit = sortedTxs.find(t =>
                          t.trade_number === current.trade_number &&
                          !ENTRY_TYPES.has(t.type)
                        );
                        pairedTxs.push({
                          trade_number: current.trade_number,
                          direction: isShort ? 'short' : 'long',
                          entry_date: current.date,
                          entry_price: current.price,
                          exit_date: matchingExit?.date || null,
                          exit_price: matchingExit?.price || null,
                          exit_type: matchingExit?.type || null,
                          exit_reason: matchingExit?.exit_reason || null,
                          gain: matchingExit?.gain != null ? matchingExit.gain : null,
                          cumulative_gain: matchingExit?.cumulative_gain != null ? matchingExit.cumulative_gain : current.cumulative_gain,
                          days_held: matchingExit?.days_held != null ? matchingExit.days_held : null,
                          mae: matchingExit?.mae != null ? matchingExit.mae : null,
                          leverage: matchingExit?.leverage || current.leverage || '1X',
                          is_open: !matchingExit,
                        });
                      }

                      return (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-dark-400 text-xs uppercase tracking-wider">
                            <th className="text-left py-2 px-2 font-medium whitespace-nowrap">#</th>
                            {isAesStrategy && (
                              <th className="text-left py-2 px-2 font-medium whitespace-nowrap">Lev</th>
                            )}
                            <th className="text-left py-2 px-2 font-medium whitespace-nowrap">Dir</th>
                            <th className="text-left py-2 px-2 font-medium whitespace-nowrap">Entry Date</th>
                            <th className="text-left py-2 px-2 font-medium whitespace-nowrap">Exit Date</th>
                            <th className="text-right py-2 px-2 font-medium whitespace-nowrap">Entry Price</th>
                            <th className="text-right py-2 px-2 font-medium whitespace-nowrap">Exit Price</th>
                            <th className="text-right py-2 px-2 font-medium whitespace-nowrap">Gain %</th>
                            <th className="text-right py-2 px-2 font-medium whitespace-nowrap">Days</th>
                            {hasMAE && (
                              <th className="text-right py-2 px-2 font-medium whitespace-nowrap">MAE %</th>
                            )}
                            <th className="text-right py-2 px-2 font-medium whitespace-nowrap">Cumul %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pairedTxs.map((tx, idx) => (
                            <tr key={idx} className="border-t border-dark-700/50 hover:bg-dark-800/50">
                              <td className="py-2 px-2 text-white whitespace-nowrap">{tx.trade_number}</td>
                              {isAesStrategy && (
                                <td className="py-2 px-2">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    tx.leverage === '2X' ? 'bg-amber-900/40 text-amber-400 border border-amber-700/50' :
                                    tx.leverage === '3X' ? 'bg-rose-900/40 text-rose-400 border border-rose-700/50' :
                                    'bg-blue-900/40 text-blue-400 border border-blue-700/50'
                                  }`}>
                                    {tx.leverage || '1X'}
                                  </span>
                                </td>
                              )}
                              <td className="py-2 px-2 whitespace-nowrap">
                                <DirectionBadge direction={tx.direction || 'long'} />
                                {tx.exit_reason && tx.direction === 'short' && (
                                  <ExitReasonPill reason={tx.exit_reason} className="ml-1.5" />
                                )}
                              </td>
                              <td className="py-2 px-2 text-dark-200 whitespace-nowrap">{formatDate(tx.entry_date)}</td>
                              <td className="py-2 px-2 whitespace-nowrap">
                                {tx.is_open ? (
                                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-900/40 text-emerald-400 border border-emerald-700/50">OPEN</span>
                                ) : tx.exit_type && tx.exit_type.includes('CLOSE') ? (
                                  <span className="text-dark-200">{formatDate(tx.exit_date)} <span className="text-xs text-amber-400">(Close)</span></span>
                                ) : (
                                  <span className="text-dark-200">{formatDate(tx.exit_date)}</span>
                                )}
                              </td>
                              <td className="py-2 px-2 text-right text-dark-200 whitespace-nowrap">
                                {tx.entry_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="py-2 px-2 text-right text-dark-200 whitespace-nowrap">
                                {tx.exit_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '-'}
                              </td>
                              <td className={`py-2 px-2 text-right font-medium whitespace-nowrap ${
                                tx.gain === null ? 'text-dark-500' :
                                tx.gain > 0 ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {tx.gain !== null ? `${tx.gain > 0 ? '+' : ''}${tx.gain.toFixed(2)}%` : '-'}
                              </td>
                              <td className="py-2 px-2 text-right text-dark-300 whitespace-nowrap">
                                {tx.days_held !== null ? tx.days_held : '-'}
                              </td>
                              {hasMAE && (
                                <td className="py-2 px-2 text-right font-medium text-orange-400 whitespace-nowrap">
                                  {tx.mae !== null && tx.mae !== undefined ? `${tx.mae.toFixed(2)}%` : '-'}
                                </td>
                              )}
                              <td className={`py-2 px-2 text-right font-medium whitespace-nowrap ${
                                tx.cumulative_gain > 0 ? 'text-green-400' : 'text-red-400'
                              }`}>
                                {tx.cumulative_gain > 0 ? '+' : ''}{tx.cumulative_gain?.toFixed(2)}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

// Tab 1: Yearly Statistics Report Component
const YearlyStatisticsReport = ({ data, filters, onYearClick }) => {
  // Sorting state: 'asc' (oldest first) or 'desc' (newest first)
  const [sortOrder, setSortOrder] = React.useState('asc');

  if (!data || !data.yearly_returns) {
    return null;
  }

  // Toggle sort order
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Critical years configuration for India market
  // Market Regime Rules (applied to years BEFORE 2021):
  // - Bear Markets: ≤ -8%
  // - Corrections: -8% to 0%
  // Dynamic classification based on index return:
  // - Bear Market: < -10%
  // - Correction: -10% to -5%
  // - Sideways: -5% to +5%
  // - Bull/Uptrend: > +5%
  // - Recent: 2021-2026 (always shown as Recent regardless of return)
  const classifyByIndexReturn = (indexReturn, year) => {
    // Recent years are always classified as "Recent"
    if (year >= 2021) return 'recent';

    if (indexReturn < -10) return 'bear';
    if (indexReturn >= -10 && indexReturn < -5) return 'correction';
    if (indexReturn >= -5 && indexReturn <= 5) return 'sideways';
    return 'bull'; // > 5%
  };

  // Helper to get year category for display (uses dynamic classification based on index return)
  const getYearCategoryByReturn = (indexReturn, year) => {
    const category = classifyByIndexReturn(indexReturn, year);
    const categoryConfig = {
      bear: { label: 'Bear Market', color: 'text-red-400 bg-red-900/30 border-red-800/50' },
      correction: { label: 'Correction', color: 'text-orange-400 bg-orange-900/30 border-orange-800/50' },
      sideways: { label: 'Sideways', color: 'text-yellow-400 bg-yellow-900/30 border-yellow-800/50' },
      bull: { label: 'Bull Market', color: 'text-green-400 bg-green-900/30 border-green-800/50' },
      recent: { label: 'Recent', color: 'text-sky-400 bg-sky-900/30 border-sky-800/50' }
    };
    return categoryConfig[category] || null;
  };

  // Filter years based on performance filter
  let filteredYears = [...data.yearly_returns];
  let groupedYears = null; // For critical years grouped view

  if (filters.performanceFilter === 'custom') {
    // Custom year range filter
    filteredYears = filteredYears.filter(yr => {
      const yearInRange =
        (!filters.fromYear || yr.year >= filters.fromYear) &&
        (!filters.toYear || yr.year <= filters.toYear);
      return yearInRange;
    });
  } else if (filters.performanceFilter === 'worst') {
    // Worst performers: negative returns OR didn't beat index
    filteredYears = filteredYears.filter(yr =>
      yr.strategy_return < 0 || yr.strategy_return < yr.index_return
    );
  } else if (filters.performanceFilter === 'best') {
    // Best performers: positive returns AND beat index
    filteredYears = filteredYears.filter(yr =>
      yr.strategy_return > 0 && yr.strategy_return > yr.index_return
    );
  } else if (filters.performanceFilter === 'critical') {
    // Dynamic classification based on index_return values - works for all markets
    groupedYears = [
      {
        category: 'Bear Markets',
        color: 'text-red-400',
        bgColor: 'bg-red-900/20 border-red-800/50',
        years: filteredYears.filter(yr => classifyByIndexReturn(yr.index_return, yr.year) === 'bear')
      },
      {
        category: 'Corrections',
        color: 'text-orange-400',
        bgColor: 'bg-orange-900/20 border-orange-800/50',
        years: filteredYears.filter(yr => classifyByIndexReturn(yr.index_return, yr.year) === 'correction')
      },
      {
        category: 'Sideways',
        color: 'text-yellow-400',
        bgColor: 'bg-yellow-900/20 border-yellow-800/50',
        years: filteredYears.filter(yr => classifyByIndexReturn(yr.index_return, yr.year) === 'sideways')
      },
      {
        category: 'Bull Markets',
        color: 'text-green-400',
        bgColor: 'bg-green-900/20 border-green-800/50',
        years: filteredYears.filter(yr => classifyByIndexReturn(yr.index_return, yr.year) === 'bull')
      },
      {
        category: 'Recent',
        color: 'text-sky-400',
        bgColor: 'bg-sky-900/20 border-sky-800/50',
        years: filteredYears.filter(yr => classifyByIndexReturn(yr.index_return, yr.year) === 'recent')
      }
    ].filter(group => group.years.length > 0);
  }

  // Apply sorting to filteredYears (for non-grouped views)
  if (!groupedYears) {
    filteredYears = [...filteredYears].sort((a, b) => {
      return sortOrder === 'asc' ? a.year - b.year : b.year - a.year;
    });
  }

  // Export function for yearly statistics
  // Matches exactly what the UI table displays
  const exportYearlyStats = () => {
    if (!filteredYears || filteredYears.length === 0) return;

    const workbook = XLSX.utils.book_new();

    // Get filter type for filename and sheet name
    const filterType = filters.performanceFilter === 'custom'
      ? `${filters.fromYear || 'start'}_to_${filters.toYear || 'end'}`
      : filters.performanceFilter;

    // Helper to format return values exactly like the UI
    const formatReturn = (value) => {
      if (value === null || value === undefined) return '0.00%';
      const num = parseFloat(value);
      return `${num > 0 ? '+' : ''}${num.toFixed(2)}%`;
    };

    const formatWinRate = (value) => {
      if (value === null || value === undefined) return '0.0%';
      return `${parseFloat(value).toFixed(1)}%`;
    };

    // Check if any year has MAE data
    const hasMAE = filteredYears.some(yr => yr.avg_mae !== undefined || yr.max_mae !== undefined);

    // Create headers matching UI table exactly
    const headers = ['Year', 'Trades', 'Strategy Return', 'Index Return', 'Wins', 'Losses', 'Win Rate', '% In Market'];
    if (hasMAE) {
      headers.push('Avg MAE', 'Max MAE');
    }

    const formatMAE = (value) => {
      if (value === null || value === undefined) return '';
      return `${parseFloat(value).toFixed(2)}%`;
    };

    const formatTimeInMarket = (value) => {
      if (value === null || value === undefined) return '-';
      return `${parseFloat(value).toFixed(0)}%`;
    };

    // If critical years view with grouping
    if (groupedYears && filters.performanceFilter === 'critical') {
      // Create rows with category grouping
      const rows = [];
      groupedYears.forEach(group => {
        // Add category header row
        const emptyRow = hasMAE ? ['', '', '', '', '', '', '', '', '', ''] : ['', '', '', '', '', '', '', ''];
        emptyRow[0] = group.category;
        rows.push(emptyRow);
        // Add years in this category
        group.years.forEach(year => {
          const row = [
            year.year,
            year.total_trades || 0,
            formatReturn(year.strategy_return),
            formatReturn(year.index_return),
            year.winning_trades || 0,
            year.losing_trades || 0,
            formatWinRate(year.win_rate),
            formatTimeInMarket(year.time_in_market_pct)
          ];
          if (hasMAE) {
            row.push(formatMAE(year.avg_mae), formatMAE(year.max_mae));
          }
          rows.push(row);
        });
      });

      const sheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      const cols = [{ wch: 15 }, { wch: 10 }, { wch: 16 }, { wch: 14 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 12 }];
      if (hasMAE) {
        cols.push({ wch: 10 }, { wch: 10 });
      }
      worksheet['!cols'] = cols;
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Critical Years');
    } else {
      // Regular flat view
      const rows = filteredYears.map(year => {
        const row = [
          year.year,
          year.total_trades || 0,
          formatReturn(year.strategy_return),
          formatReturn(year.index_return),
          year.winning_trades || 0,
          year.losing_trades || 0,
          formatWinRate(year.win_rate),
          formatTimeInMarket(year.time_in_market_pct)
        ];
        if (hasMAE) {
          row.push(formatMAE(year.avg_mae), formatMAE(year.max_mae));
        }
        return row;
      });

      const sheetData = [headers, ...rows];
      const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
      const cols = [{ wch: 8 }, { wch: 10 }, { wch: 16 }, { wch: 14 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 12 }];
      if (hasMAE) {
        cols.push({ wch: 10 }, { wch: 10 });
      }
      worksheet['!cols'] = cols;
      XLSX.utils.book_append_sheet(workbook, worksheet, `${filterType} Years`);
    }

    // Generate filename with symbol, strategy, and filter type
    const dateStr = new Date().toISOString().split('T')[0];
    const symbolName = filters.symbol || 'unknown';
    const strategyName = filters.strategy || '1x';
    const filename = `${symbolName}_${strategyName}_yearly_stats_${filterType}_${dateStr}.xlsx`;

    // Download the file
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="space-y-5">
      {/* Summary Stats - Only show when viewing All Years */}
      {filters.performanceFilter === 'all' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-dark-800 rounded-lg p-5 border border-dark-700">
            <div className="flex items-center space-x-2 mb-2">
              <BarChart3 className="w-5 h-5 text-primary-400" />
              <span className="text-sm font-semibold text-dark-300 uppercase tracking-wide">Total Trades</span>
            </div>
            <p className="text-3xl font-bold text-white">{data.total_trades || 0}</p>
            <p className="text-sm text-dark-400 mt-1">{data.total_winning_trades}W / {data.total_losing_trades}L across {data.num_years} years</p>
          </div>

          {/* NOTE: strategy_return changes based on selected strategy (1X, 2X, AES)
              The backend returns different values in yearly_returns based on filters.strategy */}
          <div className="bg-dark-800 rounded-lg p-5 border border-dark-700">
            <div className="flex items-center space-x-2 mb-2">
              <TrendingUp className="w-5 h-5 text-yellow-400" />
              <span className="text-sm font-semibold text-dark-300 uppercase tracking-wide">Avg Gain Per Year</span>
            </div>
            <p className={`text-3xl font-bold ${(data.yearly_returns.reduce((sum, yr) => sum + (yr.strategy_return || 0), 0) / data.num_years) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {(data.yearly_returns.reduce((sum, yr) => sum + (yr.strategy_return || 0), 0) / data.num_years).toFixed(1)}%
            </p>
            <p className="text-sm text-dark-400 mt-1">Average strategy return per year</p>
          </div>

          {/* Avg Time in Market */}
          <div className="bg-teal-950/40 rounded-lg p-5 border border-teal-700/50">
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="w-5 h-5 text-teal-400" />
              <span className="text-sm font-semibold text-teal-300 uppercase tracking-wide">Avg Time in Market</span>
            </div>
            <p className="text-3xl font-bold text-teal-400">
              {data.yearly_returns.length > 0
                ? `${(data.yearly_returns.reduce((sum, yr) => sum + (yr.time_in_market_pct || 0), 0) / data.yearly_returns.length).toFixed(0)}%`
                : '-'}
            </p>
            <p className="text-sm text-dark-400 mt-1">of year exposed to market</p>
          </div>

          <div className="bg-dark-800 rounded-lg p-5 border border-dark-700">
            <div className="flex items-center space-x-2 mb-2">
              <Trophy className="w-5 h-5 text-green-400" />
              <span className="text-sm font-semibold text-dark-300 uppercase tracking-wide">Years Outperformed</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {((data.yearly_returns.filter(yr => yr.strategy_return > yr.index_return).length / data.num_years) * 100).toFixed(0)}%
            </p>
            <p className="text-sm text-dark-400 mt-1">
              {data.yearly_returns.filter(yr => yr.strategy_return > yr.index_return).length}/{data.num_years} years beat index
            </p>
          </div>
        </div>
      )}

      {/* Yearly Comparison Table */}
      <div className="bg-dark-800 rounded-lg p-4 border border-dark-700">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <h4 className="text-lg font-bold text-white">Year-by-Year Comparison</h4>
            {data?.net_costs && (
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-900/40 text-amber-300 border border-amber-700/50"
                title="Per-year strategy return, win rate, avg gain/loss are net of impact + carry + interest costs. Index Return stays gross."
              >
                After Costs
              </span>
            )}
            {filters.performanceFilter !== 'all' && (
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                filters.performanceFilter === 'best'
                  ? 'bg-green-900/30 text-green-400 border border-green-800/50'
                  : filters.performanceFilter === 'worst'
                    ? 'bg-red-900/30 text-red-400 border border-red-800/50'
                    : filters.performanceFilter === 'custom'
                      ? 'bg-purple-900/30 text-purple-400 border border-purple-800/50'
                      : 'bg-yellow-900/30 text-yellow-400 border border-yellow-800/50'
              }`}>
                {filters.performanceFilter === 'best'
                  ? 'Best'
                  : filters.performanceFilter === 'worst'
                    ? 'Worst'
                    : filters.performanceFilter === 'custom'
                      ? `${filters.fromYear || 'Start'} - ${filters.toYear || 'End'}`
                      : 'Critical'}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 text-sm">
              <div className="flex items-center space-x-1.5">
                <span className="bg-green-500/20 px-2 py-1 rounded"></span>
                <span className="text-dark-400">Beat</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <span className="bg-red-500/20 px-2 py-1 rounded"></span>
                <span className="text-dark-400">Under</span>
              </div>
            </div>
            <div className="text-sm text-dark-400 border-l border-dark-700 pl-3">
              {filteredYears.length}/{data.yearly_returns.length} years
            </div>
            <button
              onClick={exportYearlyStats}
              className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors font-medium"
              title="Export to Excel"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Export Excel</span>
            </button>
          </div>
        </div>

        {/* Check if any year has MAE data */}
        {(() => {
          const hasMAEData = filteredYears.some(yr => yr.avg_mae !== undefined || yr.max_mae !== undefined);
          const colCount = hasMAEData ? 11 : 9;
          return (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-dark-800 z-10">
              <tr className="border-b-2 border-dark-700">
                <th className="text-left py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">
                  {!groupedYears ? (
                    <button
                      onClick={toggleSortOrder}
                      className="flex items-center gap-1 hover:text-white transition-colors cursor-pointer"
                      title={sortOrder === 'desc' ? 'Showing newest first - click for oldest first' : 'Showing oldest first - click for newest first'}
                    >
                      Year
                      {sortOrder === 'desc' ? (
                        <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronUp className="w-3 h-3" />
                      )}
                    </button>
                  ) : (
                    'Year'
                  )}
                </th>
                <th className="text-right py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Trades</th>
                <th className="text-right py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Strategy Return</th>
                <th className="text-right py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Index Return</th>
                <th className="text-right py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Wins</th>
                <th className="text-right py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Losses</th>
                <th className="text-right py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Win Rate</th>
                <th className="text-right py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider" title="Percentage of year in market (total days held / 365)">% In Market</th>
                {hasMAEData && (
                  <>
                    <th className="text-right py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Avg MAE</th>
                    <th className="text-right py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Max MAE</th>
                  </>
                )}
                <th className="text-right py-3 px-3 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider"></th>
              </tr>
            </thead>
            <tbody>
              {/* Grouped view for Critical Years */}
              {groupedYears ? (
                groupedYears.map((group) => (
                  <React.Fragment key={group.category}>
                    {/* Section Header */}
                    <tr className={`border ${group.bgColor}`}>
                      <td colSpan={colCount} className={`py-2 px-3 font-semibold text-sm ${group.color}`}>
                        {group.category}
                        <span className="ml-2 text-dark-400 font-normal">({group.years.length} years)</span>
                      </td>
                    </tr>
                    {/* Years in this group */}
                    {group.years.map((year) => {
                      const diff = year.strategy_return - year.index_return;
                      const beatIndex = diff > 0;
                      const positiveReturns = year.strategy_return > 0;
                      const cellStyle = beatIndex
                        ? 'bg-green-500/20 border-l-2 border-green-400'
                        : 'bg-red-500/20 border-l-2 border-red-400';

                      return (
                        <tr key={year.year} className="border-b border-dark-800 hover:bg-dark-700/50 group">
                          <td className="py-2.5 px-3 pl-6">
                            <button
                              onClick={() => onYearClick && onYearClick(year.year)}
                              className="text-white font-medium hover:text-primary-400 hover:underline cursor-pointer flex items-center gap-1 transition-colors"
                            >
                              {year.year}
                              <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                            </button>
                          </td>
                          <td className="py-2.5 px-3 text-right text-white">{year.total_trades || 0}</td>
                          <td className={`py-2.5 px-3 text-right font-semibold ${cellStyle} ${
                            positiveReturns ? 'text-green-300' : 'text-red-300'
                          }`}>
                            {year.strategy_return > 0 ? '+' : ''}{year.strategy_return.toFixed(2)}%
                          </td>
                          <td className={`py-2.5 px-3 text-right ${
                            year.index_return > 0 ? 'text-blue-300' : 'text-blue-400'
                          }`}>
                            {year.index_return > 0 ? '+' : ''}{year.index_return.toFixed(2)}%
                          </td>
                          <td className="py-2.5 px-3 text-right text-green-400">{year.winning_trades || 0}</td>
                          <td className="py-2.5 px-3 text-right text-red-400">{year.losing_trades || 0}</td>
                          <td className={`py-2.5 px-3 text-right font-semibold ${
                            (year.win_rate || 0) >= 50 ? 'text-green-400' : 'text-red-400'
                          }`}>
                            {year.win_rate?.toFixed(1) || '0.0'}%
                          </td>
                          <td className="py-2.5 px-3 text-right text-teal-400" title={year.total_days ? `${year.total_days} total days held` : ''}>
                            {year.time_in_market_pct !== undefined ? `${year.time_in_market_pct.toFixed(0)}%` : '-'}
                          </td>
                          {hasMAEData && (
                            <>
                              <td className="py-2.5 px-3 text-right text-orange-400">
                                {year.avg_mae !== undefined ? `${year.avg_mae.toFixed(2)}%` : '-'}
                              </td>
                              <td className="py-2.5 px-3 text-right text-orange-400">
                                {year.max_mae !== undefined ? `${year.max_mae.toFixed(2)}%` : '-'}
                              </td>
                            </>
                          )}
                          <td className="py-2.5 px-3 text-right">
                            <button
                              onClick={() => onYearClick && onYearClick(year.year)}
                              className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1 transition-colors hover:underline ml-auto"
                            >
                              View Transactions
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))
              ) : (
                /* Regular flat view for All/Best/Worst */
                filteredYears.map((year) => {
                  const diff = year.strategy_return - year.index_return;
                  const beatIndex = diff > 0;
                  const positiveReturns = year.strategy_return > 0;
                  const cellStyle = beatIndex
                    ? 'bg-green-500/20 border-l-2 border-green-400'
                    : 'bg-red-500/20 border-l-2 border-red-400';

                  return (
                    <tr key={year.year} className="border-b border-dark-800 hover:bg-dark-700/50 group">
                      <td className="py-2.5 px-3">
                        <button
                          onClick={() => onYearClick && onYearClick(year.year)}
                          className="text-white font-medium hover:text-primary-400 hover:underline cursor-pointer flex items-center gap-1 transition-colors"
                        >
                          {year.year}
                          <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-50" />
                        </button>
                      </td>
                      <td className="py-2.5 px-3 text-right text-white">{year.total_trades || 0}</td>
                      <td className={`py-2.5 px-3 text-right font-semibold ${cellStyle} ${
                        positiveReturns ? 'text-green-300' : 'text-red-300'
                      }`}>
                        {year.strategy_return > 0 ? '+' : ''}{year.strategy_return.toFixed(2)}%
                      </td>
                      <td className={`py-2.5 px-3 text-right ${
                        year.index_return > 0 ? 'text-blue-300' : 'text-blue-400'
                      }`}>
                        {year.index_return > 0 ? '+' : ''}{year.index_return.toFixed(2)}%
                      </td>
                      <td className="py-2.5 px-3 text-right text-green-400">{year.winning_trades || 0}</td>
                      <td className="py-2.5 px-3 text-right text-red-400">{year.losing_trades || 0}</td>
                      <td className={`py-2.5 px-3 text-right font-semibold ${
                        (year.win_rate || 0) >= 50 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {year.win_rate?.toFixed(1) || '0.0'}%
                      </td>
                      <td className="py-2.5 px-3 text-right text-teal-400" title={year.total_days ? `${year.total_days} total days held` : ''}>
                        {year.time_in_market_pct !== undefined ? `${year.time_in_market_pct.toFixed(0)}%` : '-'}
                      </td>
                      {hasMAEData && (
                        <>
                          <td className="py-2.5 px-3 text-right text-orange-400">
                            {year.avg_mae !== undefined ? `${year.avg_mae.toFixed(2)}%` : '-'}
                          </td>
                          <td className="py-2.5 px-3 text-right text-orange-400">
                            {year.max_mae !== undefined ? `${year.max_mae.toFixed(2)}%` : '-'}
                          </td>
                        </>
                      )}
                      <td className="py-2.5 px-3 text-right">
                        <button
                          onClick={() => onYearClick && onYearClick(year.year)}
                          className="text-primary-400 hover:text-primary-300 text-sm flex items-center gap-1 transition-colors hover:underline ml-auto"
                        >
                          View Transactions
                          <ExternalLink className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
          );
        })()}
      </div>
    </div>
  );
};

// Multi-Index Comparison View - Shows best performing strategy with crown icon
const YearlyComparisonReport = ({ comparisonData, filters, allMarketSymbols, onRemoveSymbol }) => {
  // Sorting state: 'desc' (newest first) is default for this view
  const [sortOrder, setSortOrder] = React.useState('desc');

  // Get all available indexes from all markets for the chip selector (must be before early return)
  const allAvailableIndexes = React.useMemo(() => {
    const indexes = [];
    const marketOrder = ['india', 'us', 'asia_pacific', 'europe', 'crypto', 'precious_metals'];
    marketOrder.forEach(market => {
      if (allMarketSymbols && allMarketSymbols[market]) {
        allMarketSymbols[market].forEach(symbol => {
          indexes.push({ ...symbol, market });
        });
      }
    });
    return indexes;
  }, [allMarketSymbols]);

  if (!comparisonData || Object.keys(comparisonData).length === 0) {
    return null;
  }

  // Get market flag for a symbol
  const getSymbolFlag = (symbolCode) => {
    for (const [market, symbols] of Object.entries(allMarketSymbols || {})) {
      const symbol = symbols.find(s => s.symbol_code === symbolCode);
      if (symbol) {
        const flags = {
          'india': '🇮🇳',
          'us': '🇺🇸',
          'asia_pacific': '🌏',
          'europe': '🇪🇺'
        };
        return flags[market] || '';
      }
    }
    return '';
  };

  // Get symbol display names from all markets
  const getSymbolName = (symbolCode) => {
    for (const market of Object.values(allMarketSymbols || {})) {
      const symbol = market.find(s => s.symbol_code === symbolCode);
      if (symbol) return symbol.symbol_name;
    }
    return symbolCode;
  };

  // Collect all unique years from all indexes
  const allYears = new Set();
  filters.symbols.forEach(symbolCode => {
    const symbolData = comparisonData[symbolCode];
    if (symbolData?.yearly_returns) {
      symbolData.yearly_returns.forEach(yr => allYears.add(yr.year));
    }
  });

  // Sort years (newest first by default)
  const sortedYears = Array.from(allYears).sort((a, b) =>
    sortOrder === 'asc' ? a - b : b - a
  );

  // Get year data for a specific symbol
  const getYearData = (symbolCode, year) => {
    const symbolData = comparisonData[symbolCode];
    if (!symbolData?.yearly_returns) return null;
    return symbolData.yearly_returns.find(yr => yr.year === year);
  };

  // Format return value
  const formatReturn = (value) => {
    if (value === null || value === undefined) return { text: '-', color: 'text-dark-500', num: null };
    const num = parseFloat(value);
    if (isNaN(num)) return { text: '-', color: 'text-dark-500', num: null };
    const color = num >= 0 ? 'text-green-400' : 'text-red-400';
    const text = `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
    return { text, color, num };
  };

  // Export comparison data to Excel
  const exportToExcel = () => {
    const symbolsList = filters.symbols.filter(s => comparisonData[s]);
    if (symbolsList.length === 0) return;

    const workbook = XLSX.utils.book_new();

    // Collect all years
    const allYearsSet = new Set();
    symbolsList.forEach(symbolCode => {
      const symbolData = comparisonData[symbolCode];
      if (symbolData?.yearly_returns) {
        symbolData.yearly_returns.forEach(yr => allYearsSet.add(yr.year));
      }
    });
    const years = Array.from(allYearsSet).sort((a, b) => b - a); // Newest first

    // Build header row: Year | Index1 Index | Index1 Strategy | Index2 Index | ...
    const headers = ['Year'];
    symbolsList.forEach(symbolCode => {
      const name = getSymbolName(symbolCode);
      headers.push(`${name} Index`);
      headers.push(`${name} Strategy`);
    });

    // Build data rows
    const rows = years.map(year => {
      const row = [year];
      symbolsList.forEach(symbolCode => {
        const yearData = getYearData(symbolCode, year);
        const indexVal = yearData?.index_return;
        const strategyVal = yearData?.strategy_return;
        row.push(indexVal !== null && indexVal !== undefined ? `${parseFloat(indexVal).toFixed(1)}%` : '-');
        row.push(strategyVal !== null && strategyVal !== undefined ? `${parseFloat(strategyVal).toFixed(1)}%` : '-');
      });
      return row;
    });

    // Create worksheet
    const sheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

    // Set column widths
    const colWidths = [{ wch: 8 }]; // Year column
    symbolsList.forEach(() => {
      colWidths.push({ wch: 14 }); // Index column
      colWidths.push({ wch: 14 }); // Strategy column
    });
    worksheet['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Comparison');

    // Generate filename
    const dateStr = new Date().toISOString().split('T')[0];
    const indexNames = symbolsList.map(s => getSymbolName(s).replace(/\s+/g, '')).join('_');
    const filename = `compare_returns_${indexNames}_${dateStr}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  const symbolsList = filters.symbols.filter(s => comparisonData[s]);

  // Calculate diversified (equal-weighted) returns for a year
  // Only includes indexes that have data for that year
  const getDiversifiedReturns = (year) => {
    let indexSum = 0, strategySum = 0, timeInMarketSum = 0;
    let indexCount = 0, strategyCount = 0, timeInMarketCount = 0;

    symbolsList.forEach(symbolCode => {
      const yearData = getYearData(symbolCode, year);
      const indexVal = parseFloat(yearData?.index_return);
      const strategyVal = parseFloat(yearData?.strategy_return);
      const timeInMarket = parseFloat(yearData?.time_in_market_pct);

      if (!isNaN(indexVal)) {
        indexSum += indexVal;
        indexCount++;
      }
      if (!isNaN(strategyVal)) {
        strategySum += strategyVal;
        strategyCount++;
      }
      if (!isNaN(timeInMarket)) {
        timeInMarketSum += timeInMarket;
        timeInMarketCount++;
      }
    });

    return {
      index: indexCount > 0 ? indexSum / indexCount : null,
      strategy: strategyCount > 0 ? strategySum / strategyCount : null,
      timeInMarket: timeInMarketCount > 0 ? timeInMarketSum / timeInMarketCount : null,
      indexCount,
      strategyCount,
      timeInMarketCount,
      totalSelected: symbolsList.length
    };
  };

  // Calculate overall average diversified returns across all years
  const getOverallDiversifiedReturns = () => {
    let totalIndex = 0, totalStrategy = 0, totalTimeInMarket = 0;
    let countIndex = 0, countStrategy = 0, countTimeInMarket = 0;

    sortedYears.forEach(year => {
      const div = getDiversifiedReturns(year);
      if (div.index !== null) {
        totalIndex += div.index;
        countIndex++;
      }
      if (div.strategy !== null) {
        totalStrategy += div.strategy;
        countStrategy++;
      }
      if (div.timeInMarket !== null) {
        totalTimeInMarket += div.timeInMarket;
        countTimeInMarket++;
      }
    });

    return {
      avgIndex: countIndex > 0 ? totalIndex / countIndex : null,
      avgStrategy: countStrategy > 0 ? totalStrategy / countStrategy : null,
      avgTimeInMarket: countTimeInMarket > 0 ? totalTimeInMarket / countTimeInMarket : null,
      years: countIndex
    };
  };

  const overallDiversified = getOverallDiversifiedReturns();

  return (
    <div className="space-y-6">
      {/* Header with title and export button */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Compare Returns</h2>
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors font-medium text-sm"
        >
          <Download className="w-4 h-4" />
          Export Excel
        </button>
      </div>

      {/* Index selector chips - show all available, highlight selected */}
      <div className="flex flex-wrap items-center gap-2">
        {allAvailableIndexes.map(index => {
          const isSelected = filters.symbols.includes(index.symbol_code);
          const isDisabled = !isSelected && filters.symbols.length >= 4;

          return (
            <button
              key={index.symbol_code}
              onClick={() => !isDisabled && onRemoveSymbol && onRemoveSymbol(index.symbol_code)}
              disabled={isDisabled}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                isSelected
                  ? 'bg-teal-600 text-white'
                  : isDisabled
                  ? 'bg-dark-800 text-dark-600 cursor-not-allowed'
                  : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              <span>{getSymbolFlag(index.symbol_code)}</span>
              <span>{index.symbol_name}</span>
              {isSelected && <span className="text-white">✓</span>}
            </button>
          );
        })}
        <span className="text-dark-500 text-sm ml-2">Max 4</span>
      </div>

      {/* Summary Stats Cards */}
      {symbolsList.length > 0 && overallDiversified.avgStrategy !== null && (() => {
        // Calculate additional stats
        let positiveStrategyYears = 0;
        let negativeStrategyYears = 0;
        let worstStrategyReturn = Infinity;
        let worstStrategyYear = null;
        let worstIndexReturn = Infinity;
        let worstIndexYear = null;

        sortedYears.forEach(year => {
          const div = getDiversifiedReturns(year);
          if (div.strategy !== null) {
            if (div.strategy >= 0) positiveStrategyYears++;
            else negativeStrategyYears++;
            if (div.strategy < worstStrategyReturn) {
              worstStrategyReturn = div.strategy;
              worstStrategyYear = year;
            }
          }
          if (div.index !== null && div.index < worstIndexReturn) {
            worstIndexReturn = div.index;
            worstIndexYear = year;
          }
        });
        return (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {/* Diversified Index Avg */}
            <div className="bg-dark-800/80 rounded-lg p-4 border border-dark-700">
              <div className="text-xs text-dark-400 uppercase mb-1">Avg Index Return</div>
              <div className={`text-2xl font-bold ${overallDiversified.avgIndex >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {overallDiversified.avgIndex >= 0 ? '+' : ''}{overallDiversified.avgIndex.toFixed(1)}%
              </div>
              <div className="text-xs text-dark-500">per year ({overallDiversified.years}y)</div>
            </div>

            {/* Diversified Strategy Avg */}
            <div className="bg-cyan-950/40 rounded-lg p-4 border border-cyan-700/50">
              <div className="text-xs text-cyan-400 uppercase mb-1">Avg Strategy Return</div>
              <div className={`text-2xl font-bold ${overallDiversified.avgStrategy >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {overallDiversified.avgStrategy >= 0 ? '+' : ''}{overallDiversified.avgStrategy.toFixed(1)}%
              </div>
              <div className="text-xs text-dark-500">per year ({overallDiversified.years}y)</div>
            </div>

            {/* Avg Time in Market */}
            <div className="bg-teal-950/40 rounded-lg p-4 border border-teal-700/50">
              <div className="text-xs text-teal-400 uppercase mb-1">Avg Time in Market</div>
              <div className="text-2xl font-bold text-teal-400">
                {overallDiversified.avgTimeInMarket !== null ? `${overallDiversified.avgTimeInMarket.toFixed(0)}%` : '-'}
              </div>
              <div className="text-xs text-dark-500">of year exposed</div>
            </div>

            {/* Positive vs Negative Years */}
            <div className="bg-dark-800/80 rounded-lg p-4 border border-dark-700">
              <div className="text-xs text-dark-400 uppercase mb-1">Profitable Years</div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold text-green-400">{positiveStrategyYears}</span>
                <span className="text-dark-500">/</span>
                <span className="text-lg font-bold text-red-400">{negativeStrategyYears}</span>
              </div>
              <div className="text-xs text-dark-500">positive / negative</div>
            </div>

            {/* Worst Year Comparison */}
            <div className="bg-red-950/30 rounded-lg p-4 border border-red-900/50">
              <div className="text-xs text-red-400 uppercase mb-2">Worst Year</div>
              <div className="flex items-center gap-3">
                {/* Strategy Worst */}
                <div className="flex-1">
                  <div className="text-xs text-dark-500 mb-0.5">Strategy</div>
                  <div className="text-xl font-bold text-red-400">
                    {worstStrategyReturn !== Infinity ? `${worstStrategyReturn.toFixed(1)}%` : '-'}
                  </div>
                  <div className="text-[10px] text-dark-500">{worstStrategyYear || '-'}</div>
                </div>
                <div className="text-dark-600 text-lg">vs</div>
                {/* Index Worst */}
                <div className="flex-1 text-right">
                  <div className="text-xs text-dark-500 mb-0.5">Index</div>
                  <div className="text-xl font-bold text-red-400">
                    {worstIndexReturn !== Infinity ? `${worstIndexReturn.toFixed(1)}%` : '-'}
                  </div>
                  <div className="text-[10px] text-dark-500">{worstIndexYear || '-'}</div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Comparison Table */}
      <div className="bg-dark-800 rounded-xl border border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              {/* Symbol Headers Row */}
              <tr className="border-b border-dark-600 bg-dark-900/50">
                <th className="py-4 px-4 text-left text-dark-400 font-medium text-xs uppercase w-20">
                  YEAR
                </th>
                <th className="py-4 px-3 text-center text-white font-semibold text-xs uppercase border-l-2 border-r-2 border-cyan-600 bg-cyan-950/50" colSpan={3}>
                  📊 DIVERSIFIED (Equal Weight)
                </th>
                {symbolsList.map(symbolCode => (
                  <th
                    key={symbolCode}
                    className="py-4 px-4 text-center text-white font-semibold text-sm border-l border-dark-600"
                    colSpan={2}
                  >
                    <span className="mr-2">{getSymbolFlag(symbolCode)}</span>
                    {getSymbolName(symbolCode)}
                  </th>
                ))}
              </tr>
              {/* Sub-headers Row */}
              <tr className="border-b border-dark-700 bg-dark-900/30">
                <th className="py-2 px-4"></th>
                <th className="py-2 px-3 text-center text-cyan-300 font-medium text-xs uppercase border-l-2 border-cyan-600 bg-cyan-950/30">
                  INDEX
                </th>
                <th className="py-2 px-3 text-center text-cyan-300 font-medium text-xs uppercase bg-cyan-950/30">
                  STRATEGY
                </th>
                <th className="py-2 px-3 text-center text-teal-300 font-medium text-xs uppercase border-r-2 border-cyan-600 bg-teal-950/30">
                  % IN MKT
                </th>
                {symbolsList.map(symbolCode => (
                  <React.Fragment key={symbolCode}>
                    <th className="py-2 px-4 text-center text-dark-400 font-medium text-xs uppercase border-l border-dark-700">
                      INDEX
                    </th>
                    <th className="py-2 px-4 text-center text-dark-400 font-medium text-xs uppercase">
                      STRATEGY
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedYears.map((year, idx) => {
                const diversified = getDiversifiedReturns(year);
                const rowBg = idx % 2 === 0 ? 'bg-dark-800/50' : 'bg-dark-900/30';

                return (
                  <tr
                    key={year}
                    className={`border-b border-dark-700/50 ${rowBg}`}
                  >
                    <td className="py-4 px-4 font-semibold text-white">{year}</td>
                    {/* Diversified Index Return */}
                    <td className={`py-4 px-3 text-center border-l-2 border-cyan-600 bg-cyan-950/40 ${diversified.index !== null ? (diversified.index >= 0 ? 'text-green-400' : 'text-red-400') : 'text-dark-500'}`}>
                      <div>
                        {diversified.index !== null ? `${diversified.index >= 0 ? '+' : ''}${diversified.index.toFixed(1)}%` : '-'}
                        {diversified.indexCount < diversified.totalSelected && diversified.indexCount > 0 && (
                          <div className="text-[10px] text-cyan-400/60">({diversified.indexCount}/{diversified.totalSelected})</div>
                        )}
                      </div>
                    </td>
                    {/* Diversified Strategy Return */}
                    <td className={`py-4 px-3 text-center bg-cyan-950/40 font-bold text-lg ${diversified.strategy !== null ? (diversified.strategy >= 0 ? 'text-green-400' : 'text-red-400') : 'text-dark-500'}`}>
                      <div>
                        {diversified.strategy !== null ? `${diversified.strategy >= 0 ? '+' : ''}${diversified.strategy.toFixed(1)}%` : '-'}
                        {diversified.strategyCount < diversified.totalSelected && diversified.strategyCount > 0 && (
                          <div className="text-[10px] text-cyan-400/60 font-normal">({diversified.strategyCount}/{diversified.totalSelected})</div>
                        )}
                      </div>
                    </td>
                    {/* Diversified % In Market */}
                    <td className="py-4 px-3 text-center border-r-2 border-cyan-600 bg-teal-950/40 text-teal-400 font-medium">
                      {diversified.timeInMarket !== null ? `${diversified.timeInMarket.toFixed(0)}%` : '-'}
                    </td>
                    {symbolsList.map(symbolCode => {
                      const yearData = getYearData(symbolCode, year);
                      const indexReturn = formatReturn(yearData?.index_return);
                      const strategyReturn = formatReturn(yearData?.strategy_return);

                      // Per-index cell background: red tint when index is down, green tint when up
                      const indexDown = indexReturn.num !== null && indexReturn.num < 0;
                      const cellBg = indexDown ? 'bg-red-950/40' : 'bg-emerald-950/25';

                      return (
                        <React.Fragment key={symbolCode}>
                          <td className={`py-4 px-4 text-center border-l border-dark-700/50 ${indexReturn.color} ${cellBg}`}>
                            {indexReturn.text}
                          </td>
                          <td className={`py-4 px-4 text-center ${strategyReturn.color} ${cellBg}`}>
                            {strategyReturn.text}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend and Summary footer */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-4 text-dark-400">
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-red-950/40 border border-dark-600"></span>
            <span>Down year</span>
          </span>
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-emerald-950/25 border border-dark-600"></span>
            <span>Up year</span>
          </span>
          <span className="text-dark-500 ml-2">|</span>
          <span className="text-dark-500">Equal-weight allocation across {symbolsList.length} indexes</span>
        </div>
        {overallDiversified.avgStrategy !== null && (
          <div className="text-dark-400 bg-dark-700/50 px-3 py-1.5 rounded-lg">
            <span className="text-dark-500">Avg Diversified: </span>
            <span className="text-white">Index </span>
            <span className={overallDiversified.avgIndex >= 0 ? 'text-green-400' : 'text-red-400'}>
              {overallDiversified.avgIndex >= 0 ? '+' : ''}{overallDiversified.avgIndex.toFixed(1)}%
            </span>
            <span className="mx-2 text-dark-600">→</span>
            <span className="text-white">Strategy </span>
            <span className={`font-bold ${overallDiversified.avgStrategy >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {overallDiversified.avgStrategy >= 0 ? '+' : ''}{overallDiversified.avgStrategy.toFixed(1)}%
            </span>
            <span className="text-dark-500 ml-2">({overallDiversified.years}y)</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Tab 2: Performance Summary Report Component
const PerformanceSummaryReport = ({ data, strategy = '1x', symbol = '' }) => {
  if (!data) {
    return null;
  }

  // Helper to format CAGR values
  const formatCagr = (value) => {
    if (value === null || value === undefined) return '-';
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num >= 0 ? `${num.toFixed(2)}%` : `${num.toFixed(2)}%`;
  };

  // Get color class based on value comparison
  const getCagrColorClass = (mvValue, indexValue) => {
    if (mvValue === null || indexValue === null) return 'text-white';
    const mv = parseFloat(mvValue);
    const idx = parseFloat(indexValue);
    if (isNaN(mv) || isNaN(idx)) return 'text-white';
    return mv > idx ? 'text-green-400' : 'text-red-400';
  };

  // Get the MV CAGR value based on selected strategy
  const getMvCagr = (period) => {
    if (strategy === '1x') return period.marketvibes_cagr;
    if (strategy === '2x') return period.marketvibes_2x_cagr;
    if (strategy === 'aes_moderate') return period.marketvibes_aes_moderate_cagr;
    if (strategy === 'aes_aggressive') return period.marketvibes_aes_aggressive_cagr;
    return null;
  };

  // Strategy display config
  const strategyConfig = {
    '1x': { label: 'MV 1X', color: 'text-blue-400' },
    'aes_moderate': { label: 'MV AES Moderate', color: 'text-purple-400' },
    'aes_aggressive': { label: 'MV AES Aggressive', color: 'text-rose-400' }
  };

  const currentStrategy = strategyConfig[strategy] || strategyConfig['1x'];

  // Export function for performance summary - exports ALL strategies
  const exportPerformanceSummary = () => {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: CAGR by Period - ALL 4 strategies
    if (data.cagr_by_period && data.cagr_by_period.length > 0) {
      const cagrHeaders = ['Period', 'Index CAGR', 'MV 1X', 'MV 2X', 'AES Moderate', 'AES Aggressive'];
      const cagrRows = data.cagr_by_period.map(period => [
        period.period,
        formatCagr(period.index_cagr),
        formatCagr(period.marketvibes_cagr),
        formatCagr(period.marketvibes_2x_cagr),
        formatCagr(period.marketvibes_aes_moderate_cagr),
        formatCagr(period.marketvibes_aes_aggressive_cagr)
      ]);
      const cagrData = [cagrHeaders, ...cagrRows];
      const cagrSheet = XLSX.utils.aoa_to_sheet(cagrData);
      cagrSheet['!cols'] = [{ wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(workbook, cagrSheet, 'CAGR by Period');
    }

    // Sheet 2: Trade Statistics (for currently selected strategy)
    if (data.trade_statistics) {
      const stats = data.trade_statistics;
      const tradeData = [
        ['Metric', 'Value', 'Notes'],
        ['Strategy', currentStrategy.label, ''],
        ['Total Trades', stats.total_trades || 'N/A', ''],
        ['Trades per Year', stats.trades_per_year || 'N/A', ''],
        ['Win Rate', stats.win_rate || 'N/A', ''],
        ['Winning Trades', stats.winning_trades || 0, ''],
        ['Losing Trades', stats.losing_trades || 0, ''],
        ['Avg Return/Trade', stats.avg_return_per_trade || 'N/A', ''],
        ['Avg Gain (Wins)', stats.avg_gain_wins || 'N/A', ''],
        ['Avg Loss (Losses)', stats.avg_loss_losses || 'N/A', ''],
        ['Max Gain', stats.max_gain || 'N/A', formatDate(stats.max_gain_date) || ''],
        ['Max Loss', stats.max_loss || 'N/A', formatDate(stats.max_loss_date) || ''],
        ['Avg Days Held', stats.avg_days_held || 'N/A', ''],
        ['Avg MAE', stats.avg_mae || 'N/A', ''],
        ['Max MAE', stats.max_mae || 'N/A', formatDate(stats.max_mae_date) || '']
      ];
      const tradeSheet = XLSX.utils.aoa_to_sheet(tradeData);
      tradeSheet['!cols'] = [{ wch: 18 }, { wch: 18 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, tradeSheet, 'Trade Statistics');
    }

    // Generate filename (no strategy suffix since CAGR has all strategies)
    const dateStr = new Date().toISOString().split('T')[0];
    const symbolName = symbol || 'unknown';
    const filename = `${symbolName}_performance_summary_${dateStr}.xlsx`;

    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="space-y-5">
      {/* Period Information with Export Button */}
      <div className="flex items-center justify-between">
        {data.period && (
          <div className="bg-dark-800/50 rounded-lg p-3 border border-dark-700">
            <span className="text-sm font-semibold text-dark-300">Trading Period: </span>
            <span className="text-sm text-white font-medium">
              {formatDate(data.period.first_trade_date)} to {formatDate(data.period.last_trade_date)} ({data.period.years} years)
            </span>
          </div>
        )}
        <button
          onClick={exportPerformanceSummary}
          className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors font-medium"
          title="Export to Excel"
        >
          <Download className="w-4 h-4" />
          <span className="text-sm">Export Excel</span>
        </button>
      </div>

      {/* Side-by-Side Layout: Performance + Trade Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Left: Performance Comparison Table - Period by Period */}
        <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-primary-400" />
            <span>Performance Comparison</span>
            {data?.net_costs && (
              <span
                className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-900/40 text-amber-300 border border-amber-700/50"
                title="Strategy CAGR is net of impact + carry + interest costs. Index CAGR stays gross."
              >
                After Costs
              </span>
            )}
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-3 px-4 text-dark-400 font-medium text-sm">Period</th>
                  <th className="text-right py-3 px-4 text-blue-400 font-medium text-sm">Index CAGR</th>
                  <th className={`text-right py-3 px-4 ${currentStrategy.color} font-medium text-sm`}>{currentStrategy.label}</th>
                </tr>
              </thead>
              <tbody>
                {data.cagr_by_period && data.cagr_by_period.length > 0 ? (
                  data.cagr_by_period.map((period, idx) => {
                    const isSummary = period.is_summary;
                    const rowClass = isSummary
                      ? 'bg-primary-900/20 border-t-2 border-primary-700'
                      : 'border-b border-dark-800/50 hover:bg-dark-700/30';
                    const mvCagr = getMvCagr(period);

                    return (
                      <tr key={idx} className={rowClass}>
                        <td className={`py-3 px-4 font-medium ${isSummary ? 'text-primary-300' : 'text-white'}`}>
                          {period.period}
                        </td>
                        <td className="py-3 px-4 text-right text-blue-400 font-semibold">
                          {formatCagr(period.index_cagr)}
                        </td>
                        <td className={`py-3 px-4 text-right font-semibold ${getCagrColorClass(mvCagr, period.index_cagr)}`}>
                          {formatCagr(mvCagr)}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  /* Fallback to old format if cagr_by_period not available */
                  data.performance_1x && (
                    <>
                      <tr className="border-b border-dark-800/50">
                        <td className="py-4 px-4 text-white font-medium">CAGR</td>
                        <td className="py-4 px-4 text-right text-blue-400 font-bold">{data.performance_1x.index_cagr}</td>
                        <td className={`py-4 px-4 text-right font-bold ${currentStrategy.color}`}>
                          {strategy === '1x' ? data.performance_1x.cagr :
                           strategy === '2x' ? data.performance_2x?.cagr :
                           strategy === 'aes_moderate' ? data.performance_aes_moderate?.cagr :
                           strategy === 'aes_aggressive' ? data.performance_aes_aggressive?.cagr : '-'}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-4 px-4 text-white font-medium">Outperformance</td>
                        <td className="py-4 px-4 text-right text-dark-500 font-semibold">-</td>
                        <td className={`py-4 px-4 text-right font-bold ${getCagrColorClass(
                          strategy === '1x' ? data.performance_1x.outperformance :
                          strategy === '2x' ? data.performance_2x?.outperformance :
                          strategy === 'aes_moderate' ? data.performance_aes_moderate?.outperformance :
                          strategy === 'aes_aggressive' ? data.performance_aes_aggressive?.outperformance : 0, 0
                        )}`}>
                          {strategy === '1x' ? data.performance_1x.outperformance :
                           strategy === '2x' ? data.performance_2x?.outperformance :
                           strategy === 'aes_moderate' ? data.performance_aes_moderate?.outperformance :
                           strategy === 'aes_aggressive' ? data.performance_aes_aggressive?.outperformance : '-'}
                        </td>
                      </tr>
                    </>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right: Trade Statistics Table */}
        {data.trade_statistics && (
          <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
            {/* Header with Leverage Usage Badge for AES strategies */}
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-white flex items-center">
                <span>Trade Statistics</span>
                {data?.net_costs && (
                  <span
                    className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-900/40 text-amber-300 border border-amber-700/50"
                    title="Per-trade gain, win rate, and avg gain/loss are net of impact + carry + interest."
                  >
                    After Costs
                  </span>
                )}
              </h4>
              {data.trade_statistics.leverage_stats && (strategy === 'aes_moderate' || strategy === 'aes_aggressive') && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-900/40 to-orange-900/40 border border-amber-700/50">
                  <span className="text-xs text-amber-300/80 font-medium">Leverage Used</span>
                  <span className="text-lg font-bold text-amber-400">{data.trade_statistics.leverage_stats.leverage_usage_pct}%</span>
                  <span className="text-xs text-dark-400">
                    ({data.trade_statistics.leverage_stats.leverage_2x_count}×2X{data.trade_statistics.leverage_stats.leverage_3x_count > 0 ? `, ${data.trade_statistics.leverage_stats.leverage_3x_count}×3X` : ''})
                  </span>
                </div>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <tbody>
                  <tr className="border-b border-dark-800/50">
                    <td className="py-3.5 px-4 text-white font-medium">Total Trades</td>
                    <td className="py-3.5 px-4 text-right text-white font-bold text-lg">{data.trade_statistics.total_trades || 'N/A'}</td>
                  </tr>
                  <tr className="border-b border-dark-800/50">
                    <td className="py-3.5 px-4 text-white font-medium">Trades per Year</td>
                    <td className="py-3.5 px-4 text-right text-primary-400 font-bold text-lg">
                      {data.trade_statistics.trades_per_year !== null && data.trade_statistics.trades_per_year !== undefined
                        ? data.trade_statistics.trades_per_year
                        : 'N/A'}
                    </td>
                  </tr>
                  <tr className="border-b border-dark-800/50">
                    <td className="py-3.5 px-4 text-white font-medium">Win Rate</td>
                    <td className="py-3.5 px-4 text-right text-green-400 font-bold text-lg">{data.trade_statistics.win_rate || 'N/A'}</td>
                  </tr>
                  <tr className="border-b border-dark-800/50">
                    <td className="py-3.5 px-4 text-white font-medium">Wins / Losses</td>
                    <td className="py-3.5 px-4 text-right text-white font-semibold text-base">
                      <span className="text-green-400">{data.trade_statistics.winning_trades || 0}</span>
                      <span className="text-dark-400 mx-2">/</span>
                      <span className="text-red-400">{data.trade_statistics.losing_trades || 0}</span>
                    </td>
                  </tr>
                  {data.trade_statistics.avg_return_per_trade && (
                    <tr className="border-b border-dark-800/50">
                      <td className="py-3.5 px-4 text-white font-medium">Avg Return/Trade</td>
                      <td className="py-3.5 px-4 text-right text-primary-400 font-bold text-lg">{data.trade_statistics.avg_return_per_trade}</td>
                    </tr>
                  )}
                  <tr className="border-b border-dark-800/50">
                    <td className="py-3.5 px-4 text-white font-medium">Avg Gain / Loss</td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="font-bold text-base">
                        <span className="text-green-400">{data.trade_statistics.avg_gain_wins || 'N/A'}</span>
                        <span className="text-dark-400 mx-2">/</span>
                        <span className="text-red-400">{data.trade_statistics.avg_loss_losses || 'N/A'}</span>
                      </div>
                      {data.trade_statistics.avg_gain_wins && data.trade_statistics.avg_loss_losses && (
                        <div className="text-sm text-yellow-400 font-semibold mt-1">
                          Ratio: {(Math.abs(parseFloat(data.trade_statistics.avg_gain_wins)) / Math.abs(parseFloat(data.trade_statistics.avg_loss_losses))).toFixed(2)}x
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr className="border-b border-dark-800/50">
                    <td className="py-3.5 px-4 text-white font-medium">Max Gain</td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="text-green-400 font-bold text-lg">{data.trade_statistics.max_gain || 'N/A'}</div>
                      {data.trade_statistics.max_gain_date && (
                        <div className="text-xs text-dark-400 mt-0.5">{formatDate(data.trade_statistics.max_gain_date)}</div>
                      )}
                    </td>
                  </tr>
                  <tr className="border-b border-dark-800/50">
                    <td className="py-3.5 px-4 text-white font-medium">Max Loss</td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="text-red-400 font-bold text-lg">{data.trade_statistics.max_loss || 'N/A'}</div>
                      {data.trade_statistics.max_loss_date && (
                        <div className="text-xs text-dark-400 mt-0.5">{formatDate(data.trade_statistics.max_loss_date)}</div>
                      )}
                    </td>
                  </tr>
                  <tr className="border-b border-dark-800/50">
                    <td className="py-3.5 px-4 text-white font-medium">Avg Days Held</td>
                    <td className="py-3.5 px-4 text-right text-white font-bold text-lg">{data.trade_statistics.avg_days_held || 'N/A'}</td>
                  </tr>
                  {/* MAE (Max Adverse Excursion) Metrics */}
                  {(data.trade_statistics.avg_mae || data.trade_statistics.max_mae) && (
                    <>
                      <tr className="border-b border-dark-800/50">
                        <td className="py-3.5 px-4 text-white font-medium">Avg MAE</td>
                        <td className="py-3.5 px-4 text-right text-orange-400 font-bold text-lg">{data.trade_statistics.avg_mae || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td className="py-3.5 px-4 text-white font-medium">Max MAE</td>
                        <td className="py-3.5 px-4 text-right">
                          <div className="text-orange-400 font-bold text-lg">{data.trade_statistics.max_mae || 'N/A'}</div>
                          {data.trade_statistics.max_mae_date && (
                            <div className="text-xs text-dark-400 mt-0.5">{formatDate(data.trade_statistics.max_mae_date)}</div>
                          )}
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Tab 3: All Transactions Report Component
const AllTransactionsReport = ({ data, filters, onFilterChange }) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exportingOHLC, setExportingOHLC] = useState(false);
  // Read the same shared shorts-permission/position state the parent uses,
  // so we can keep the Shorts Only switch visible whenever the user is on a
  // shorts-capable Strategy — even if the currently loaded file happens to
  // contain no short rows.
  const { mode: currentPositionMode } = usePositionMode();
  // Independent of the Strategy selector. Strategy decides which file we read;
  // this filter decides which rows from that file we render.
  //   'all'    — show longs + shorts (default)
  //   'shorts' — show only short trades from the loaded file
  // We don't expose a "longs only" option here because that's literally what
  // the "Long Only" Strategy already does, just from a different file.
  const [directionFilter, setDirectionFilter] = useStickyState(
    'reports.allTx.directionFilter',
    'all',
    (v) => v === 'all' || v === 'shorts',
  );

  if (!data || !data.transactions) {
    return null;
  }

  // Use filters from parent (persisted state)
  const filterType = filters.transactionFilterType;
  const selectedYear = filters.transactionYear;
  const startDate = filters.transactionStartDate;
  const endDate = filters.transactionEndDate;
  const fileHasShorts = data.transactions.some(t => t.direction === 'short');

  // Extract unique years from transactions
  const availableYears = [...new Set(
    data.transactions
      .filter(t => t.date)
      .map(t => new Date(t.date).getFullYear())
  )].sort((a, b) => b - a); // Sort descending (newest first)

  // Check if AES strategy is selected
  const isAesStrategy = filters.strategy === 'aes_moderate' || filters.strategy === 'aes_aggressive';

  // Helper function to apply date filter to transactions
  const applyDateFilter = (transactions) => {
    if (!selectedYear && !startDate && !endDate) {
      return transactions;
    }

    return transactions.filter(trade => {
      if (!trade.date) return true;
      const tradeDate = new Date(trade.date);

      // Year filter takes precedence if set
      if (selectedYear) {
        return tradeDate.getFullYear() === parseInt(selectedYear);
      }

      // Custom date range filter
      if (startDate || endDate) {
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        if (start && tradeDate < start) return false;
        if (end && tradeDate > end) return false;
      }

      return true;
    });
  };

  // Helper function to get trade pairs (BUY + SELL) from SELL transactions
  const getTradePartsFromSells = (sellTransactions, allTransactions) => {
    const tradeNumbersToInclude = new Set();
    sellTransactions.forEach(sell => {
      tradeNumbersToInclude.add(sell.trade_number); // The SELL
      tradeNumbersToInclude.add(sell.trade_number - 1); // The preceding BUY
    });
    return allTransactions.filter(trade => tradeNumbersToInclude.has(trade.trade_number));
  };

  // Filter transactions based on selected filters (type filter + date filter combined)
  const filteredTransactions = (() => {
    // First apply date filter to get the base set
    const dateFilteredTransactions = applyDateFilter(data.transactions);

    // With Leverage filter - show only trades that used leverage (2X or 3X)
    if (filterType === 'with_leverage' && isAesStrategy) {
      const leveragedSells = dateFilteredTransactions
        .filter(t => t.type === 'SELL' && (t.leverage === '2X' || t.leverage === '3X'));

      const result = getTradePartsFromSells(leveragedSells, dateFilteredTransactions);

      // Also include open trade with leverage (check open_trade_leverage from API)
      const openTradeLeverage = data.open_trade_leverage;
      if (openTradeLeverage === '2X' || openTradeLeverage === '3X') {
        // Find the open BUY (last BUY without matching SELL)
        const allBuys = dateFilteredTransactions.filter(t => t.type === 'BUY');
        const allSellTradeNumbers = new Set(dateFilteredTransactions.filter(t => t.type !== 'BUY').map(t => t.trade_number));
        const openBuy = allBuys.find(buy => !allSellTradeNumbers.has(buy.trade_number + 1));
        if (openBuy && !result.find(t => t.trade_number === openBuy.trade_number)) {
          result.push(openBuy);
        }
      }
      return result;
    }

    // ALL gain-based filters (Losses / Top 10 Losses / Top 10 Profits /
    // Consecutive Losses / Consecutive Gains) MUST include both long exits
    // (SELL) and short exits (SHORT COVER, SHORT COVER (Forced)). Otherwise
    // the With Rec. Shorts view silently drops every short trade from these
    // filters, which is exactly the bug we're fixing here.
    const EXIT_TYPES = new Set(['SELL', 'SHORT COVER', 'SHORT COVER (Forced)']);
    const isExitRow = (t) => EXIT_TYPES.has(t.type);

    // Losses filter - show both entries and exits for any losing trade.
    if (filterType === 'losses') {
      const losingExits = dateFilteredTransactions
        .filter(t => isExitRow(t) && t.gain !== null && t.gain !== undefined && t.gain < 0);
      return getTradePartsFromSells(losingExits, dateFilteredTransactions);
    }

    // Top 10 Losses (worst single-trade losses across longs + shorts).
    if (filterType === 'top10losses') {
      const top10LosingExits = dateFilteredTransactions
        .filter(t => isExitRow(t) && t.gain !== null && t.gain !== undefined && t.gain < 0)
        .sort((a, b) => a.gain - b.gain)
        .slice(0, 10);
      return getTradePartsFromSells(top10LosingExits, dateFilteredTransactions);
    }

    // Top 10 Profits (best single-trade gains across longs + shorts).
    if (filterType === 'top10profits') {
      const top10ProfitExits = dateFilteredTransactions
        .filter(t => isExitRow(t) && t.gain !== null && t.gain !== undefined && t.gain > 0)
        .sort((a, b) => b.gain - a.gain)
        .slice(0, 10);
      return getTradePartsFromSells(top10ProfitExits, dateFilteredTransactions);
    }

    // Consecutive Losses — across the full chronology of exits, not just longs.
    if (filterType === 'consecutive_losses') {
      const exitTransactions = dateFilteredTransactions.filter(isExitRow);
      const sortedExits = [...exitTransactions].sort((a, b) => a.trade_number - b.trade_number);

      const consecutiveLossExits = [];
      let currentStreak = [];
      for (let i = 0; i < sortedExits.length; i++) {
        const isLoss = sortedExits[i].gain !== null && sortedExits[i].gain !== undefined && sortedExits[i].gain < 0;
        if (isLoss) {
          currentStreak.push(sortedExits[i]);
        } else {
          if (currentStreak.length >= 2) consecutiveLossExits.push(...currentStreak);
          currentStreak = [];
        }
      }
      if (currentStreak.length >= 2) consecutiveLossExits.push(...currentStreak);

      return getTradePartsFromSells(consecutiveLossExits, dateFilteredTransactions);
    }

    // Consecutive Gains — same fix.
    if (filterType === 'consecutive_gains') {
      const sellTransactions = dateFilteredTransactions.filter(isExitRow);
      const sortedSells = [...sellTransactions].sort((a, b) => a.trade_number - b.trade_number);

      // Find consecutive gain streaks (2 or more gains in a row)
      const consecutiveGainSells = [];
      let currentStreak = [];

      for (let i = 0; i < sortedSells.length; i++) {
        const isGain = sortedSells[i].gain !== null && sortedSells[i].gain !== undefined && sortedSells[i].gain > 0;

        if (isGain) {
          currentStreak.push(sortedSells[i]);
        } else {
          // End of streak - if we had 2+ consecutive gains, add them
          if (currentStreak.length >= 2) {
            consecutiveGainSells.push(...currentStreak);
          }
          currentStreak = [];
        }
      }
      // Check final streak
      if (currentStreak.length >= 2) {
        consecutiveGainSells.push(...currentStreak);
      }

      return getTradePartsFromSells(consecutiveGainSells, dateFilteredTransactions);
    }

    // Default: return date-filtered transactions
    return dateFilteredTransactions;
  })();

  // Override the sticky directionFilter to 'all' only when the user is on
  // Long Only mode (where the Shorts Only switch is hidden). On a shorts-
  // capable Strategy the switch is always visible and we honour whatever
  // the user picks — even if the loaded file happens to have zero short
  // rows, in which case the existing "No trades found" state renders.
  const effectiveDirection = currentPositionMode === 'long_only' ? 'all' : directionFilter;

  // Create paired transactions (entry + matching exit on the same row).
  // The Direction filter (`all` vs `shorts`) only changes which entry types
  // we pair: 'all' = both BUY (long) and SHORT SELL (short); 'shorts' = SHORT
  // SELL only. There is no longs-only option here on purpose — that's what
  // the Strategy=Long Only selector already provides from a different file.
  const pairedTransactions = (() => {
    const pairs = [];
    const transactions = [...filteredTransactions].sort((a, b) => a.trade_number - b.trade_number);

    const ENTRY_TYPES = effectiveDirection === 'shorts'
      ? new Set(['SHORT SELL'])
      : new Set(['BUY', 'SHORT SELL']);

    for (let i = 0; i < transactions.length; i++) {
      const current = transactions[i];
      if (!ENTRY_TYPES.has(current.type)) continue;

      const isShort = current.type === 'SHORT SELL';
      const matchingExit = transactions.find(t =>
        t.trade_number === current.trade_number + 1 &&
        !ENTRY_TYPES.has(t.type)
      );
      // Calculate days held for open positions (entries without a matching exit)
      let daysHeld = matchingExit?.days_held != null ? matchingExit.days_held : null;
      if (!matchingExit && current.date && data.last_close_date) {
        const entryDate = new Date(current.date);
        const lastCloseDate = new Date(data.last_close_date);
        daysHeld = Math.floor((lastCloseDate - entryDate) / (1000 * 60 * 60 * 24));
      }

      pairs.push({
        trade_number: Math.ceil(current.trade_number / 2),
        direction: isShort ? 'short' : 'long',
        entry_date: current.date,
        entry_price: current.price,
        exit_date: matchingExit?.date || null,
        exit_price: matchingExit?.price || null,
        exit_type: matchingExit?.type || null,
        // ML-recommended-shorts files surface a per-trade exit reason (SL5%, TP10%, TP20%).
        exit_reason: matchingExit?.exit_reason || null,
        gain: matchingExit?.gain != null ? matchingExit.gain : null,
        cumulative_gain: matchingExit?.cumulative_gain != null ? matchingExit.cumulative_gain : current.cumulative_gain,
        days_held: daysHeld,
        mae: matchingExit?.mae != null ? matchingExit.mae : (!matchingExit ? data.open_trade_mae : null),
        leverage: matchingExit?.leverage || current.leverage || '1X',
        buy_trade_number: current.trade_number,
        sell_trade_number: matchingExit?.trade_number || null,
        is_open: !matchingExit,
        last_close: !matchingExit ? data.last_close : null,
        last_close_date: !matchingExit ? data.last_close_date : null,
        // Human model version (v1/v2/v3) — the ENTRY row's version is the trade's version.
        model_revision: current?.model_revision ?? matchingExit?.model_revision ?? null,
      });
    }

    return pairs;
  })();

  // Streaks must reflect the *original chronological* sequence — never the
  // gain-based filtered one. We build TWO streak maps:
  //   • streakAll    — across every closed pair in the file
  //   • streakShorts — across short pairs only (longs are skipped entirely)
  // The right map is picked at render time based on the user's direction
  // filter, so "Shorts Only" shows a streak that actually represents
  // consecutive winning shorts and ignores intervening long trades, while
  // the default view continues to show the true chronological streak.
  const buildStreakMap = (allPairs) => {
    const map = new Map();
    let lossMul = 1, inLossStreak = false;
    let gainMul = 1, inGainStreak = false;
    let prevGain = null;
    for (const p of allPairs) {
      const g = p.gain;
      const isLoss = g !== null && g !== undefined && g < 0;
      const isGain = g !== null && g !== undefined && g > 0;
      const prevWasLoss = prevGain !== null && prevGain < 0;
      const prevWasGain = prevGain !== null && prevGain > 0;

      let lossStreak = null;
      let gainStreak = null;
      if (isLoss) {
        if (prevWasLoss) { lossMul *= (1 + g / 100); inLossStreak = true; }
        else { lossMul = 1 + g / 100; inLossStreak = false; }
        lossStreak = inLossStreak ? (lossMul - 1) * 100 : null;
        gainMul = 1; inGainStreak = false;
      } else {
        lossMul = 1; inLossStreak = false;
      }
      if (isGain) {
        if (prevWasGain) { gainMul *= (1 + g / 100); inGainStreak = true; }
        else { gainMul = 1 + g / 100; inGainStreak = false; }
        gainStreak = inGainStreak ? (gainMul - 1) * 100 : null;
      } else if (!isLoss) {
        gainMul = 1; inGainStreak = false;
      }
      map.set(p.entryTn, { lossStreak, gainStreak });
      prevGain = g;
    }
    return map;
  };

  // Strict-adjacency streak map for the Shorts Only filter.
  //   A streak only forms between two consecutive trades that are BOTH winning
  //   (or losing) shorts in REAL chronology — i.e. no other trade of any kind
  //   sat between them. Two profitable shorts separated by one or more long
  //   trades are NOT back-to-back, so the second one shows '—' in the Streak
  //   column. This matches the user-defined rule:
  //     "back to back shorts. Two shorts profitable with different dates
  //      with anything between them are NOT a streak."
  const buildStrictShortStreakMap = (allPairsChronological) => {
    const map = new Map();
    let gainMul = 1;
    let lossMul = 1;
    let prevWinningShort = false;
    let prevLosingShort = false;
    for (const p of allPairsChronological) {
      const g = p.gain;
      const isShort = p.isShort;
      const isWin = g != null && g > 0;
      const isLoss = g != null && g < 0;

      let gainStreak = null;
      let lossStreak = null;

      if (isShort && isWin) {
        if (prevWinningShort) {
          gainMul *= 1 + g / 100;
          gainStreak = (gainMul - 1) * 100;
        } else {
          gainMul = 1 + g / 100;
        }
        prevWinningShort = true;
        prevLosingShort = false;
        lossMul = 1;
      } else if (isShort && isLoss) {
        if (prevLosingShort) {
          lossMul *= 1 + g / 100;
          lossStreak = (lossMul - 1) * 100;
        } else {
          lossMul = 1 + g / 100;
        }
        prevLosingShort = true;
        prevWinningShort = false;
        gainMul = 1;
      } else {
        // Anything else — long trade, open trade, or unscored — breaks
        // both short-streaks because the back-to-back-shorts adjacency
        // is broken by ANY trade between two shorts.
        gainMul = 1;
        lossMul = 1;
        prevWinningShort = false;
        prevLosingShort = false;
      }

      map.set(p.entryTn, { gainStreak, lossStreak });
    }
    return map;
  };

  const { streakAll, streakShorts } = (() => {
    const txs = [...(data.transactions || [])].sort((a, b) => a.trade_number - b.trade_number);
    const ENTRY_TYPES_ALL = new Set(['BUY', 'SHORT SELL']);
    const allPairs = [];
    for (let i = 0; i < txs.length; i++) {
      const t = txs[i];
      if (!ENTRY_TYPES_ALL.has(t.type)) continue;
      const exit = txs.find(x => x.trade_number === t.trade_number + 1 && !ENTRY_TYPES_ALL.has(x.type));
      if (!exit) continue;
      allPairs.push({
        entryTn: t.trade_number,
        gain: exit.gain,
        isShort: t.type === 'SHORT SELL',
      });
    }
    return {
      streakAll: buildStreakMap(allPairs),                 // chronology, any direction
      streakShorts: buildStrictShortStreakMap(allPairs),   // strict back-to-back shorts
    };
  })();

  // Pick the appropriate streak map for the active direction filter.
  //   'all'    → streakAll (chronology across every pair, any direction)
  //   'shorts' → streakShorts (only truly back-to-back shorts compound)
  const streakByEntryTradeNumber = effectiveDirection === 'shorts' ? streakShorts : streakAll;

  const tradesWithStreaks = (() => {
    return pairedTransactions
      .map((trade) => {
        const s = streakByEntryTradeNumber.get(trade.buy_trade_number) || {};
        return {
          ...trade,
          lossStreak: s.lossStreak ?? null,
          gainStreak: s.gainStreak ?? null,
          isInLossStreak: s.lossStreak != null,
          isInGainStreak: s.gainStreak != null,
        };
      })
      .reverse(); // Display newest trades first
  })();

  // Group trades into loss streaks for consecutive_losses filter
  const lossStreakGroups = (() => {
    if (filterType !== 'consecutive_losses' || tradesWithStreaks.length === 0) {
      return null;
    }

    // Date-range helpers — must NOT rely on row order. The trade table is
    // sorted newest-first, so currentGroup[0] is the latest trade in the
    // streak and currentGroup[last] is the earliest. Computing the streak's
    // displayed window from row positions reversed the dates (start after
    // end). Pick min entry / max exit by actual timestamp instead.
    const minEntryDate = (group) => {
      let best = null;
      let bestTs = Infinity;
      for (const t of group) {
        if (!t.entry_date) continue;
        const ts = new Date(t.entry_date).getTime();
        if (Number.isFinite(ts) && ts < bestTs) { bestTs = ts; best = t.entry_date; }
      }
      return best;
    };
    const maxExitDate = (group) => {
      let best = null;
      let bestTs = -Infinity;
      for (const t of group) {
        if (!t.exit_date) continue;
        const ts = new Date(t.exit_date).getTime();
        if (Number.isFinite(ts) && ts > bestTs) { bestTs = ts; best = t.exit_date; }
      }
      return best;
    };

    const groups = [];
    let currentGroup = [];
    let groupId = 1;

    // Trades are already filtered to only show consecutive loss trades
    // Group by consecutive trade numbers
    for (let i = 0; i < tradesWithStreaks.length; i++) {
      const trade = tradesWithStreaks[i];
      const prevTrade = i > 0 ? tradesWithStreaks[i - 1] : null;

      // Check if this trade is consecutive with the previous one (handles both asc and desc order)
      const isConsecutive = prevTrade && (Math.abs(trade.trade_number - prevTrade.trade_number) === 1);

      if (isConsecutive) {
        currentGroup.push(trade);
      } else {
        // Start of a new group
        if (currentGroup.length > 0) {
          // Calculate group stats
          const totalLoss = currentGroup.reduce((sum, t) => {
            // Compound the losses: (1 + loss1/100) * (1 + loss2/100) * ... - 1
            return sum * (1 + (t.gain || 0) / 100);
          }, 1);
          const compoundedLoss = (totalLoss - 1) * 100;

          const startDate = minEntryDate(currentGroup);
          const endDate = maxExitDate(currentGroup);
          groups.push({
            id: groupId++,
            trades: currentGroup,
            startDate,
            endDate,
            tradeCount: currentGroup.length,
            totalLoss: compoundedLoss,
            startYear: startDate ? new Date(startDate).getFullYear() : null,
          });
        }
        currentGroup = [trade];
      }
    }

    // Don't forget the last group
    if (currentGroup.length > 0) {
      const totalLoss = currentGroup.reduce((sum, t) => {
        return sum * (1 + (t.gain || 0) / 100);
      }, 1);
      const compoundedLoss = (totalLoss - 1) * 100;

      const startDate = minEntryDate(currentGroup);
      const endDate = maxExitDate(currentGroup);
      groups.push({
        id: groupId,
        trades: currentGroup,
        startDate,
        endDate,
        tradeCount: currentGroup.length,
        totalLoss: compoundedLoss,
        startYear: startDate ? new Date(startDate).getFullYear() : null,
      });
    }

    return groups;
  })();

  // Handlers that update parent state
  const setFilterType = (type) => {
    onFilterChange('transactionFilterType', type);
    // Don't clear date filters when changing type filter - they work together now
  };

  // Export transactions to CSV
  const exportTransactionsToCSV = () => {
    if (!tradesWithStreaks || tradesWithStreaks.length === 0) {
      alert('No trades to export');
      return;
    }

    const isAesStrategy = filters.strategy === 'aes_moderate' || filters.strategy === 'aes_aggressive';
    const isTopTradesFilter = filterType === 'losses' || filterType === 'consecutive_losses' || filterType === 'top10losses' || filterType === 'top10profits' || filterType === 'with_leverage';
    // Mirror the UI: a Direction column is only meaningful when shorts can
    // appear, i.e. anything other than the Long Only strategy.
    const includeDirection = currentPositionMode !== 'long_only';

    // Check if any trade has MAE data
    const hasMAE = tradesWithStreaks.some(t => t.mae !== null && t.mae !== undefined);

    // Build headers based on current view (paired format) - dates together, prices together
    const headers = ['Trade #'];
    if (isAesStrategy) headers.push('Leverage');
    if (includeDirection) headers.push('Direction');
    // Use neutral Entry/Exit labels when shorts can appear so the same
    // column header makes sense for both long (BUY → SELL) and short
    // (SHORT SELL → SHORT COVER) trades.
    if (includeDirection) {
      headers.push('Entry Date', 'Exit Date', 'Entry Price', 'Exit Price', 'Gain %');
    } else {
      headers.push('BUY Date', 'SELL Date', 'BUY Price', 'SELL Price', 'Gain %');
    }
    if (!isTopTradesFilter) headers.push('Streak');
    headers.push('Days Held');
    if (hasMAE) headers.push('MAE %');

    // Build rows using paired format - dates together, prices together
    const rows = tradesWithStreaks.map(trade => {
      const row = [trade.trade_number];
      if (isAesStrategy) row.push(trade.leverage || '1X');
      const isShort = trade.direction === 'short';
      if (includeDirection) row.push(isShort ? 'SHORT' : 'LONG');
      // For open trades, show last close price and unrealized gain
      const exitPrice = trade.exit_price || (trade.is_open && trade.last_close ? trade.last_close : null);
      const gain = trade.gain !== null && trade.gain !== undefined
        ? trade.gain
        : (trade.is_open && trade.last_close && trade.entry_price
            ? ((trade.last_close - trade.entry_price) / trade.entry_price * 100)
            : null);
      // Match the UI's exit-cell behavior: show "(Forced Close)" for forced
      // covers/sells so the export carries the same nuance the table does.
      const exitTypeStr = trade.exit_type || '';
      const isForcedClose = exitTypeStr.includes('CLOSE') || exitTypeStr.includes('Forced');
      const exitDateStr = trade.is_open
        ? 'OPEN'
        : (isForcedClose && formatDate(trade.exit_date)
            ? `${formatDate(trade.exit_date)} (Forced Close)`
            : (formatDate(trade.exit_date) || ''));
      row.push(
        formatDate(trade.entry_date) || '',
        exitDateStr,
        trade.entry_price?.toFixed(2) || '',
        exitPrice?.toFixed(2) || '',
        gain !== null ? `${gain > 0 ? '+' : ''}${gain.toFixed(2)}%` : ''
      );
      if (!isTopTradesFilter) {
        const streak = trade.lossStreak !== null
          ? `${trade.lossStreak.toFixed(2)}%`
          : trade.gainStreak !== null
            ? `+${trade.gainStreak.toFixed(2)}%`
            : '';
        row.push(streak);
      }
      row.push(trade.days_held !== null && trade.days_held !== undefined ? trade.days_held : '');
      if (hasMAE) {
        row.push(trade.mae !== null && trade.mae !== undefined ? `${trade.mae.toFixed(2)}%` : '');
      }
      return row;
    });

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);

    // Generate filename with filter info (type filter + date filter combined)
    const symbolName = filters.symbol || 'transactions';
    const strategyName = filters.strategy || '1x';

    // Build filter suffix from type filter
    let typeSuffix = filterType === 'all' ? 'all' :
                     filterType === 'losses' ? 'losses' :
                     filterType === 'top10losses' ? 'top10losses' :
                     filterType === 'top10profits' ? 'top10profits' :
                     filterType === 'with_leverage' ? 'with_leverage' : 'all';

    // Add date filter suffix if applicable
    let dateSuffix = '';
    if (selectedYear) {
      dateSuffix = `_${selectedYear}`;
    } else if (startDate || endDate) {
      dateSuffix = `_${startDate || 'start'}_to_${endDate || 'end'}`;
    }

    const filterSuffix = typeSuffix + dateSuffix;
    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `${symbolName}_${strategyName}_trades_${filterSuffix}_${dateStr}.csv`;

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export OHLC data to CSV
  const exportOHLC = async () => {
    if (!filters.symbol) {
      alert('No symbol selected');
      return;
    }

    setExportingOHLC(true);
    setShowExportMenu(false);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/analytics/reports/export-ohlc/?symbol=${filters.symbol}`, {
        headers: {
          'Authorization': `Token ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch OHLC data');
      }

      const result = await response.json();

      if (!result.data || result.data.length === 0) {
        alert('No OHLC data available for this symbol');
        setExportingOHLC(false);
        return;
      }

      // Build CSV content
      const headers = ['Date', 'Open', 'High', 'Low', 'Close'];
      const rows = result.data.map(row => [
        row.date,
        row.open.toFixed(2),
        row.high.toFixed(2),
        row.low.toFixed(2),
        row.close.toFixed(2)
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${filters.symbol}_ohlc_${dateStr}.csv`;

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting OHLC:', error);
      alert(`Failed to export OHLC data: ${error.message}`);
    } finally {
      setExportingOHLC(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Transactions Table */}
      <div className="bg-dark-800 rounded-lg p-4 border border-dark-700">
        {/* Header Row 1: Title + Export */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <h4 className="text-lg font-bold text-white">Transaction History</h4>
            <span className="text-sm text-dark-400">
              ({tradesWithStreaks.length}{(filterType !== 'all' || selectedYear || startDate || endDate) ? ` of ${Math.floor(data.transactions.length / 2)}` : ''} trades)
            </span>
            {data?.net_costs && (
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-900/40 text-amber-300 border border-amber-700/50"
                title="Per-trade % Gain values are net of impact + carry + interest costs."
              >
                After Costs
              </span>
            )}
            {/* Leverage Usage Indicator for AES strategies */}
            {data.leverage_stats && (filters.strategy === 'aes_moderate' || filters.strategy === 'aes_aggressive') && (
              <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-amber-900/30 text-amber-400 border border-amber-800/50" title={`${data.leverage_stats.leverage_2x_count} trades at 2X, ${data.leverage_stats.leverage_3x_count} trades at 3X out of ${data.leverage_stats.total_completed_trades} completed trades`}>
                Leverage Used: {data.leverage_stats.leverage_usage_pct}%
              </span>
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center space-x-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors font-medium"
              title="Export options"
            >
              {exportingOHLC ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="text-sm">{exportingOHLC ? 'Exporting...' : 'Export'}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-dark-700 border border-dark-600 rounded-lg shadow-xl z-[9999]">
                <button
                  onClick={() => {
                    exportTransactionsToCSV();
                    setShowExportMenu(false);
                  }}
                  className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-dark-600 rounded-t-lg flex items-center space-x-2"
                >
                  <FileText className="w-4 h-4 text-emerald-400" />
                  <span>Export All Transactions</span>
                </button>
                <button
                  onClick={exportOHLC}
                  disabled={exportingOHLC}
                  className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-dark-600 rounded-b-lg flex items-center space-x-2 border-t border-dark-600"
                >
                  {exportingOHLC ? <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" /> : <BarChart3 className="w-4 h-4 text-blue-400" />}
                  <span>{exportingOHLC ? 'Exporting...' : 'Export OHLC'}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Header Row 2: Filters */}
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-dark-700">
          {/* Trade Type Filter */}
          <div className="flex items-center space-x-1 bg-dark-700 rounded-lg p-1">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                filterType === 'all'
                  ? 'bg-primary-600 text-white'
                  : 'text-dark-300 hover:text-white'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterType('losses')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                filterType === 'losses'
                  ? 'bg-red-600 text-white'
                  : 'text-dark-300 hover:text-white'
              }`}
            >
              Losses
            </button>
            <button
              onClick={() => setFilterType('top10losses')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                filterType === 'top10losses'
                  ? 'bg-red-600 text-white'
                  : 'text-dark-300 hover:text-white'
              }`}
            >
              Top 10 Losses
            </button>
            <button
              onClick={() => setFilterType('consecutive_losses')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                filterType === 'consecutive_losses'
                  ? 'bg-orange-600 text-white'
                  : 'text-dark-300 hover:text-white'
              }`}
            >
              Consecutive Losses
            </button>
            <button
              onClick={() => setFilterType('top10profits')}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                filterType === 'top10profits'
                  ? 'bg-green-600 text-white'
                  : 'text-dark-300 hover:text-white'
              }`}
            >
              Top 10 Profits
            </button>
            {/* With Leverage - Only show for AES strategies */}
            {isAesStrategy && (
              <button
                onClick={() => setFilterType('with_leverage')}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  filterType === 'with_leverage'
                    ? 'bg-amber-600 text-white'
                    : 'text-dark-300 hover:text-white'
                }`}
              >
                With Leverage
              </button>
            )}
            {/* Shorts Only switch — always visible & enabled on shorts-capable
                Strategies. If the user flips it on for a file that has no
                short rows (e.g. a long-only copy of SENSEX upstream), the
                table simply renders empty with the existing "No trades found"
                state — that's the user's explicit ask, no need to hide or
                disable the control. */}
            {currentPositionMode !== 'long_only' && (
              <label
                className="flex items-center gap-2 ml-2 px-2 py-1 rounded cursor-pointer text-xs font-medium text-dark-200 hover:text-white"
                title="Show only short trades. Strategy decides which file is read; this filters the rendered rows."
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={directionFilter === 'shorts'}
                  onClick={() =>
                    setDirectionFilter(directionFilter === 'shorts' ? 'all' : 'shorts')
                  }
                  className={`relative w-9 h-[18px] rounded-full transition-colors flex-shrink-0 ${
                    directionFilter === 'shorts' ? 'bg-orange-500' : 'bg-dark-600'
                  }`}
                >
                  <span
                    className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-transform shadow ${
                      directionFilter === 'shorts' ? 'left-[19px]' : 'left-[2px]'
                    }`}
                  />
                </button>
                <span>Shorts Only</span>
              </label>
            )}
          </div>

          {/* Date Filter - Year OR Custom (mutually exclusive) */}
          <div className="flex items-center space-x-3">
            {/* Year Filter */}
            <div className="flex items-center space-x-1 bg-dark-700 rounded-lg p-1">
              <span className="text-xs text-dark-400 px-2">Year:</span>
              <select
                value={selectedYear}
                onChange={(e) => {
                  onFilterChange('transactionYear', e.target.value);
                  if (e.target.value) {
                    onFilterChange('transactionStartDate', '');
                    onFilterChange('transactionEndDate', '');
                  }
                }}
                className={`bg-dark-600 border-none text-xs rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer ${
                  selectedYear ? 'text-primary-400 font-medium' : 'text-dark-300'
                }`}
              >
                <option value="">All</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <span className="text-dark-500 text-xs">or</span>

            {/* Custom Date Range */}
            <div className="flex items-center space-x-2 bg-dark-700 rounded-lg p-1">
              <Calendar className="w-3.5 h-3.5 text-dark-400 ml-1" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  onFilterChange('transactionStartDate', e.target.value);
                  if (e.target.value) {
                    onFilterChange('transactionYear', '');
                  }
                }}
                className={`bg-dark-600 border-none text-xs rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500 w-28 ${
                  startDate ? 'text-primary-400' : 'text-dark-400'
                }`}
              />
              <span className="text-dark-500 text-xs">→</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  onFilterChange('transactionEndDate', e.target.value);
                  if (e.target.value) {
                    onFilterChange('transactionYear', '');
                  }
                }}
                className={`bg-dark-600 border-none text-xs rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-primary-500 w-28 ${
                  endDate ? 'text-primary-400' : 'text-dark-400'
                }`}
              />
              {(startDate || endDate) && (
                <button
                  onClick={() => {
                    onFilterChange('transactionStartDate', '');
                    onFilterChange('transactionEndDate', '');
                  }}
                  className="text-dark-400 hover:text-white text-xs px-1"
                  title="Clear dates"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Total Gains Summary */}
        {tradesWithStreaks.length > 0 && (() => {
          const completedTrades = tradesWithStreaks.filter(t => t.gain !== null && t.gain !== undefined);
          const totalGainSum = completedTrades.reduce((sum, t) => sum + t.gain, 0);

          return (
            <div className="mb-4">
              <div className="inline-block bg-dark-800/80 rounded-lg p-4 border border-dark-700">
                <div className="text-xs text-dark-400 uppercase mb-1">Total % Gains</div>
                <div className={`text-2xl font-bold ${totalGainSum >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totalGainSum >= 0 ? '+' : ''}{totalGainSum.toFixed(2)}%
                </div>
                <div className="text-xs text-dark-500">{completedTrades.length} trades</div>
              </div>
            </div>
          );
        })()}

        {/* Transactions Table */}
        {(() => {
          const isAesStrategy = filters.strategy === 'aes_moderate' || filters.strategy === 'aes_aggressive';
          const isTopTradesFilter = filterType === 'losses' || filterType === 'consecutive_losses' || filterType === 'top10losses' || filterType === 'top10profits' || filterType === 'with_leverage';
          const hasMAE = tradesWithStreaks.some(t => t.mae !== null && t.mae !== undefined);
          // colCount: 7 base columns + Version + leverage(optional) + streak(optional) + MAE(optional)
          const colCount = 8 + (isAesStrategy ? 1 : 0) + (isTopTradesFilter ? 0 : 1) + (hasMAE ? 1 : 0);

          return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-dark-400 text-xs uppercase tracking-wider">
                <th className="text-left py-3 px-2 font-medium whitespace-nowrap">Trade #</th>
                {isAesStrategy && (
                  <th className="text-left py-3 px-2 font-medium whitespace-nowrap">Leverage</th>
                )}
                <th className="text-left py-3 px-2 font-medium whitespace-nowrap">Direction</th>
                <th className="text-left py-3 px-2 font-medium whitespace-nowrap">Entry Date</th>
                <th className="text-left py-3 px-2 font-medium whitespace-nowrap">Exit Date</th>
                <th className="text-right py-3 px-2 font-medium whitespace-nowrap">Entry Price</th>
                <th className="text-right py-3 px-2 font-medium whitespace-nowrap">Exit Price</th>
                <th className="text-right py-3 px-2 font-medium whitespace-nowrap">Gain %</th>
                {!isTopTradesFilter && (
                  <th className="text-right py-3 px-2 font-medium whitespace-nowrap" title="Cumulative gain/loss during consecutive winning/losing trades">Streak</th>
                )}
                <th className="text-right py-3 px-2 font-medium whitespace-nowrap">Days Held</th>
                {hasMAE && (
                  <th className="text-right py-3 px-2 font-medium whitespace-nowrap">MAE %</th>
                )}
                <th className="text-right py-3 px-2 font-medium whitespace-nowrap" title="Model version (v1/v2/v3) that produced this trade">Version</th>
              </tr>
            </thead>
            <tbody>
              {tradesWithStreaks.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="py-8 text-center text-dark-400">
                    No trades found
                  </td>
                </tr>
              ) : filterType === 'consecutive_losses' && lossStreakGroups ? (
                // Grouped view for consecutive losses
                lossStreakGroups.map((group, groupIdx) => {
                  // Check if streak loss exceeds Index CAGR (violation)
                  const indexCagr = data.index_cagr;
                  const exceedsIndexCagr = indexCagr && Math.abs(group.totalLoss) > indexCagr;

                  return (
                  <React.Fragment key={`group-${group.id}`}>
                    {/* Group Header */}
                    <tr className={`border-t-2 border-dark-600 ${exceedsIndexCagr ? 'bg-red-900/30' : 'bg-dark-800'}`}>
                      <td colSpan={colCount} className="py-3 px-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <span className={exceedsIndexCagr ? 'text-red-400 font-semibold' : 'text-orange-400 font-semibold'}>
                                Streak #{group.id}
                              </span>
                              <span className="text-dark-400 text-sm">
                                {group.tradeCount} consecutive losses
                              </span>
                              {exceedsIndexCagr && (
                                <span className="px-2 py-0.5 bg-red-600/40 text-red-300 text-xs rounded border border-red-500/50">
                                  &gt; Index CAGR ({indexCagr?.toFixed(1)}%)
                                </span>
                              )}
                            </div>
                            <span className="text-dark-500 text-sm">
                              {formatDate(group.startDate)} → {group.endDate ? formatDate(group.endDate) : 'ongoing'}
                            </span>
                          </div>
                          <div className={`px-4 py-1.5 rounded-lg font-bold text-lg ${
                            exceedsIndexCagr
                              ? 'bg-red-600/50 text-red-300 border border-red-400/70'
                              : group.totalLoss <= -10
                                ? 'bg-red-600/30 text-red-400 border border-red-500/50'
                                : 'bg-orange-600/30 text-orange-400 border border-orange-500/50'
                          }`}>
                            {group.totalLoss.toFixed(2)}%
                          </div>
                        </div>
                      </td>
                    </tr>
                    {/* Group Trades */}
                    {group.trades.map((trade, idx) => (
                      <tr key={`${group.id}-${idx}`} className="border-t border-dark-700/50 hover:bg-dark-800/50 bg-orange-900/10">
                        <td className="py-3 px-2 text-white whitespace-nowrap">{trade.trade_number}</td>
                        {isAesStrategy && (
                          <td className="py-3 px-2 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              trade.leverage === '2X' ? 'bg-amber-900/40 text-amber-400 border border-amber-700/50' :
                              trade.leverage === '3X' ? 'bg-rose-900/40 text-rose-400 border border-rose-700/50' :
                              'bg-blue-900/40 text-blue-400 border border-blue-700/50'
                            }`}>
                              {trade.leverage || '1X'}
                            </span>
                          </td>
                        )}
                        <td className="py-3 px-2 whitespace-nowrap">
                          <DirectionBadge direction={trade.direction || 'long'} />
                          {trade.exit_reason && trade.direction === 'short' && (
                            <ExitReasonPill reason={trade.exit_reason} className="ml-1.5" />
                          )}
                        </td>
                        <td className="py-3 px-2 text-dark-200 whitespace-nowrap">{formatDate(trade.entry_date)}</td>
                        <td className="py-3 px-2 whitespace-nowrap">
                          {trade.is_open ? (
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-900/40 text-emerald-400 border border-emerald-700/50">OPEN</span>
                          ) : trade.exit_type && trade.exit_type.includes('CLOSE') ? (
                            <span className="text-dark-200">{formatDate(trade.exit_date)} <span className="text-xs text-amber-400">(Close)</span></span>
                          ) : (
                            <span className="text-dark-200">{formatDate(trade.exit_date)}</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right text-dark-200 whitespace-nowrap">{trade.entry_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '-'}</td>
                        <td className="py-3 px-2 text-right whitespace-nowrap">
                          {trade.exit_price ? (
                            <span className="text-dark-200">{trade.exit_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          ) : trade.is_open && trade.last_close ? (
                            <span className="text-emerald-400" title={`Last close: ${formatDate(trade.last_close_date)}`}>
                              {trade.last_close.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          ) : (
                            <span className="text-dark-400">-</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-right font-medium whitespace-nowrap text-red-400">
                          {trade.gain !== null && trade.gain !== undefined
                            ? `${trade.gain.toFixed(2)}%`
                            : '-'}
                        </td>
                        <td className={`py-3 px-2 text-right whitespace-nowrap ${
                          trade.is_open && trade.days_held !== null ? 'text-emerald-400' : 'text-dark-300'
                        }`}>
                          {trade.days_held !== null && trade.days_held !== undefined ? trade.days_held : '-'}
                        </td>
                        {hasMAE && (
                          <td className={`py-3 px-2 text-right font-medium whitespace-nowrap ${
                            trade.mae !== null && trade.mae !== undefined ? 'text-orange-400' : 'text-dark-400'
                          }`}>
                            {trade.mae !== null && trade.mae !== undefined ? `${trade.mae.toFixed(2)}%` : '-'}
                          </td>
                        )}
                        <td className="py-3 px-2 text-dark-300 whitespace-nowrap">{trade.model_revision != null ? `v${trade.model_revision}` : '-'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );})
              ) : (
                // Regular view for other filters
                tradesWithStreaks.map((trade, idx) => {
                  // Check if this is a consecutive loss (current loss AND previous was also a loss)
                  const isConsecutiveLoss = trade.gain !== null && trade.gain < 0 &&
                    idx > 0 && tradesWithStreaks[idx - 1].gain !== null && tradesWithStreaks[idx - 1].gain < 0;
                  // Check if this is a consecutive gain (current gain AND previous was also a gain)
                  const isConsecutiveGain = trade.gain !== null && trade.gain > 0 &&
                    idx > 0 && tradesWithStreaks[idx - 1].gain !== null && tradesWithStreaks[idx - 1].gain > 0;

                  return (
                  <tr key={idx} className={`border-t border-dark-700/50 hover:bg-dark-800/50 ${isConsecutiveLoss ? 'bg-red-900/20' : isConsecutiveGain ? 'bg-green-900/20' : ''}`}>
                    <td className="py-3 px-2 text-white whitespace-nowrap">{trade.trade_number}</td>
                    {isAesStrategy && (
                      <td className="py-3 px-2 whitespace-nowrap">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          trade.leverage === '2X' ? 'bg-amber-900/40 text-amber-400 border border-amber-700/50' :
                          trade.leverage === '3X' ? 'bg-rose-900/40 text-rose-400 border border-rose-700/50' :
                          'bg-blue-900/40 text-blue-400 border border-blue-700/50'
                        }`}>
                          {trade.leverage || '1X'}
                        </span>
                      </td>
                    )}
                    <td className="py-3 px-2 whitespace-nowrap">
                      <DirectionBadge direction={trade.direction || 'long'} />
                      {trade.exit_reason && trade.direction === 'short' && (
                        <ExitReasonPill reason={trade.exit_reason} className="ml-1.5" />
                      )}
                    </td>
                    <td className="py-3 px-2 text-dark-200 whitespace-nowrap">{formatDate(trade.entry_date)}</td>
                    <td className="py-3 px-2 whitespace-nowrap">
                      {trade.is_open ? (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-900/40 text-emerald-400 border border-emerald-700/50">OPEN</span>
                      ) : trade.exit_type && trade.exit_type.includes('CLOSE') ? (
                        <span className="text-dark-200">{formatDate(trade.exit_date)} <span className="text-xs text-amber-400">(Close)</span></span>
                      ) : (
                        <span className="text-dark-200">{formatDate(trade.exit_date)}</span>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right text-dark-200 whitespace-nowrap">{trade.entry_price?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '-'}</td>
                    <td className="py-3 px-2 text-right whitespace-nowrap">
                      {trade.exit_price ? (
                        <span className="text-dark-200">{trade.exit_price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      ) : trade.is_open && trade.last_close ? (
                        <span className="text-emerald-400" title={`Last close: ${formatDate(trade.last_close_date)}`}>
                          {trade.last_close.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      ) : (
                        <span className="text-dark-400">-</span>
                      )}
                    </td>
                    <td className={`py-3 px-2 text-right font-medium whitespace-nowrap ${
                      trade.gain === null || trade.gain === undefined
                        ? (trade.is_open && trade.last_close && trade.entry_price
                            ? ((trade.last_close - trade.entry_price) / trade.entry_price * 100) >= 0 ? 'text-emerald-400' : 'text-red-400'
                            : 'text-dark-400')
                        : trade.gain > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {trade.gain !== null && trade.gain !== undefined
                        ? `${trade.gain > 0 ? '+' : ''}${trade.gain.toFixed(2)}%`
                        : trade.is_open && trade.last_close && trade.entry_price
                          ? (() => {
                              const unrealizedGain = ((trade.last_close - trade.entry_price) / trade.entry_price * 100);
                              return `${unrealizedGain >= 0 ? '+' : ''}${unrealizedGain.toFixed(2)}%`;
                            })()
                          : '-'}
                    </td>
                    {!isTopTradesFilter && (
                      <td className={`py-3 px-2 text-right font-bold whitespace-nowrap ${
                        trade.lossStreak !== null
                          ? trade.lossStreak <= -5
                            ? 'bg-red-600/40 text-red-300 rounded'
                            : 'bg-orange-600/30 text-orange-300 rounded'
                          : trade.gainStreak !== null
                            ? trade.gainStreak >= 5
                              ? 'bg-green-600/40 text-green-300 rounded'
                              : 'bg-teal-600/30 text-teal-300 rounded'
                            : 'text-dark-500'
                      }`}>
                        {trade.lossStreak !== null
                          ? `${trade.lossStreak.toFixed(2)}%`
                          : trade.gainStreak !== null
                            ? `+${trade.gainStreak.toFixed(2)}%`
                            : '-'}
                      </td>
                    )}
                    <td className={`py-3 px-2 text-right whitespace-nowrap ${
                      trade.is_open && trade.days_held !== null ? 'text-emerald-400' : 'text-dark-300'
                    }`}>
                      {trade.days_held !== null && trade.days_held !== undefined ? trade.days_held : '-'}
                    </td>
                    {hasMAE && (
                      <td className={`py-3 px-2 text-right font-medium whitespace-nowrap ${
                        trade.mae !== null && trade.mae !== undefined ? 'text-orange-400' : 'text-dark-400'
                      }`}>
                        {trade.mae !== null && trade.mae !== undefined ? `${trade.mae.toFixed(2)}%` : '-'}
                      </td>
                    )}
                    <td className="py-3 px-2 text-dark-300 whitespace-nowrap">{trade.model_revision != null ? `v${trade.model_revision}` : '-'}</td>
                  </tr>
                );})
              )}
            </tbody>
          </table>
        </div>
          );
        })()}
      </div>
    </div>
  );
};

// All Indexes Comparison Component with Tabs
const AllIndexesComparison = () => {
  // Sub-tab is URL-driven so /reports/all-indexes?tab=outperformance_summary
  // lands on Period Performance directly (and survives a refresh / share link).
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_SUB_TABS = ['performance_summary', 'outperformance_summary'];
  const urlSubTab = searchParams.get('tab');
  const activeTab = VALID_SUB_TABS.includes(urlSubTab) ? urlSubTab : 'performance_summary';
  const setActiveTab = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const tabs = [
    { id: 'performance_summary', label: 'Performance Summary', icon: TrendingUp },
    { id: 'outperformance_summary', label: 'Period Performance', icon: Trophy }
  ];

  return (
    <div className="dashboard-card">
      {/* Tabs */}
      <div className="flex items-center space-x-1 border-b border-dark-700 pb-0 mb-4">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-1.5 px-4 py-2 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-white'
                  : 'border-transparent text-dark-400 hover:text-dark-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="font-medium text-sm">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'performance_summary' && <PerformanceSummaryTab />}
      {activeTab === 'outperformance_summary' && <OutperformanceSummaryTab />}
    </div>
  );
};

// Performance Summary Tab (existing All Indexes table)
const PerformanceSummaryTab = () => {
  // Market options — fetched from backend (respects is_active + user permissions)
  const { marketOptions: baseMarkets } = useAvailableMarkets();
  const allowedMarkets = getAllowedMarkets();

  // Add "All Markets" option at the front
  const marketOptions = baseMarkets.length > 1
    ? [{ value: 'all', label: 'All Markets', icon: '🌍' }, ...baseMarkets]
    : baseMarkets;

  // Default to first allowed market (not 'all' for restricted users)
  const defaultMarket = allowedMarkets.includes('all') ? 'all' : (baseMarkets[0]?.value || 'india');

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Sticky filter — survives a page refresh.
  const [marketFilter, setMarketFilter] = useStickyState('reports.summary.marketFilter', defaultMarket);
  const [exporting, setExporting] = useState(false);

  // Strategy toggle — gated to apalshah, persisted across navigation. Three options:
  // long_only | all_shorts | rec_shorts. Backend always reads 1X Stats from the
  // matching folder; AES leverage variants are no longer surfaced.
  const { mode: positionMode, canUseShorts, isShortsActive } = usePositionMode();
  const showShortsUI = isShortsActive;

  // Same sticky-state key as the Individual Indexes view so the toggle
  // setting flows across pages without re-clicking.
  const [netCosts, setNetCosts] = useStickyState('reports.netCosts', false);

  // Click-to-open cost-breakdown popover. Tracks which symbol_code is open;
  // null means closed. We dismiss on outside-click via a document handler.
  const [costInfoSymbol, setCostInfoSymbol] = useState(null);
  useEffect(() => {
    if (!costInfoSymbol) return;
    const onDocClick = (e) => {
      if (!e.target.closest || !e.target.closest('[data-cost-popover]')) {
        setCostInfoSymbol(null);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [costInfoSymbol]);

  // Helper to check if user can see a specific market
  const canSeeMarket = (market) => {
    if (allowedMarkets.includes('all')) return true;
    return allowedMarkets.includes(market);
  };

  // Helper to filter symbols based on user's allowed symbols for a market
  const filterMarketData = (marketData, market) => {
    if (!marketData) return [];
    return marketData.filter(index => isSymbolAllowed(market, index.symbol_code));
  };

  useEffect(() => {
    fetchAllIndexes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionMode, netCosts]);

  const exportToExcel = async () => {
    setExporting(true);
    try {
    const workbook = XLSX.utils.book_new();

    // Single strategy run (1X). The "strategies" loop is preserved as a 1-item
    // array so the rest of the export structure below stays intact.
    const positionLabels = {
      long_only: 'Long Only',
      all_shorts: 'With Shorts',
      rec_shorts: 'With Rec. Shorts',
    };
    const strategies = [
      { key: '1x', label: positionLabels[positionMode] || 'Long Only' }
    ];

    // All markets (filtered by user permissions)
    const allMarketsData = [
      { key: 'india', name: 'India' },
      { key: 'us', name: 'US' },
      { key: 'crypto', name: 'Crypto' },
      { key: 'asia_pacific', name: 'Asia Pacific' },
      { key: 'europe', name: 'Europe' },
      { key: 'precious_metals', name: 'Precious Metals' }
    ];

    // Filter markets based on user selection AND permissions
    const markets = marketFilter === 'all'
      ? allMarketsData.filter(m => canSeeMarket(m.key))
      : allMarketsData.filter(m => m.key === marketFilter && canSeeMarket(m.key));

    const isSingleMarket = marketFilter !== 'all';

    // Helper function to create sheet data for a market
    const createMarketSheet = (marketData, strategyLabel) => {
      if (!marketData || marketData.length === 0) return null;

      const headers = [
        'Index', 'Since', 'Index CAGR', `MV ${strategyLabel} CAGR`, 'Outperformance',
        'Trades/Year', 'Avg Hold (days)', 'Win Rate', 'Avg Return',
        'Avg Gain', 'Avg Loss', 'Gain/Loss Ratio', 'Expectancy Ratio', 'Best Gain', 'Worst Loss', 'Worst Loss Date',
        'Avg MAE', 'Max MAE'
      ];

      const rows = marketData.map(index => {
        const avgGainValue = parseFloat(index.avg_gain);
        const avgLossValue = parseFloat(index.avg_loss);
        const avgReturnValue = parseFloat(index.avg_return_per_trade);
        const gainLossRatio = avgGainValue && avgLossValue
          ? (Math.abs(avgGainValue) / Math.abs(avgLossValue)).toFixed(2)
          : 'N/A';
        // Expectancy Ratio = Expectancy / Avg Loss = AVG RET / Avg Loss
        const expectancyRatio = (avgReturnValue && avgLossValue)
          ? (avgReturnValue / Math.abs(avgLossValue)).toFixed(2)
          : 'N/A';

        return [
          index.symbol_name,
          index.since_year,
          index.index_cagr || 'N/A',
          index.marketvibes_cagr || 'N/A',
          index.outperformance || 'N/A',
          index.trades_per_year || 'N/A',
          index.avg_days_held || 'N/A',
          index.win_rate || 'N/A',
          index.avg_return_per_trade || 'N/A',
          index.avg_gain || 'N/A',
          index.avg_loss || 'N/A',
          gainLossRatio,
          expectancyRatio,
          index.best_gain || 'N/A',
          index.worst_loss || 'N/A',
          formatDate(index.worst_loss_date) || 'N/A',
          index.avg_mae || 'N/A',
          index.max_mae || 'N/A'
        ];
      });

      return [headers, ...rows];
    };

    // Fetch data for all strategies and create sheets
    const exportToken = localStorage.getItem('adminToken');
    for (const strat of strategies) {
      try {
        const queryParams = new URLSearchParams({
          position_mode: positionMode,
        });

        const response = await fetch(`${API_BASE_URL}/analytics/reports/all-indexes-comparison/?${queryParams}`, {
          cache: 'no-store',
          headers: exportToken ? { 'Authorization': `Basic ${exportToken}` } : {},
        });

        if (!response.ok) continue;

        const strategyData = await response.json();

        // Create a sheet for each selected market with this strategy
        markets.forEach(market => {
          const sheetData = createMarketSheet(strategyData.markets?.[market.key], strat.label);
          if (sheetData) {
            const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
            worksheet['!cols'] = [
              { wch: 20 }, { wch: 8 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
              { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 12 },
              { wch: 10 }, { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
              { wch: 10 }, { wch: 10 }
            ];
            // If single market selected, use just strategy name; otherwise include market name
            const sheetName = isSingleMarket ? strat.label : `${market.name} ${strat.label}`;
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
          }
        });
      } catch (err) {
        console.error(`Error fetching ${strat.key} data:`, err);
      }
    }

    // Filename: date + selected market + position mode (no leverage segment, all 1X now)
    const dateStr = new Date().toISOString().split('T')[0];
    const marketName = isSingleMarket ? `_${marketFilter}` : '';
    const filename = `performance_summary${marketName}_${positionMode}_${dateStr}.xlsx`;

    // Download the file
    XLSX.writeFile(workbook, filename);
    } finally {
      setExporting(false);
    }
  };

  const fetchAllIndexes = async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        position_mode: positionMode,
      });
      if (netCosts) queryParams.set('net_costs', 'true');

      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/analytics/reports/all-indexes-comparison/?${queryParams}`, {
        cache: 'no-store',
        headers: token ? { 'Authorization': `Basic ${token}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const comparisonData = await response.json();
      setData(comparisonData);
    } catch (error) {
      console.error('Error loading all indexes comparison:', error);
      setError(error.message || 'Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  };

  // Single column header for the strategy CAGR column. Color shifts based on
  // which positions strategy is active so the table reads at a glance.
  const strategyHeader = positionMode === 'rec_shorts'
    ? { label: 'MV CAGR', color: 'text-orange-300' }
    : positionMode === 'all_shorts'
      ? { label: 'MV CAGR', color: 'text-amber-300' }
      : { label: 'MV CAGR', color: 'text-blue-400' };
  const currentStrategy = strategyHeader;

  // Get CAGR and outperformance - the API now returns strategy-specific data
  // in the main fields based on the strategy query parameter
  const getStrategyCagr = (index) => {
    return index.marketvibes_cagr;
  };

  const getStrategyOutperf = (index) => {
    return index.outperformance;
  };

  const renderMarketTable = (marketName, marketData, icon) => {
    if (!marketData || marketData.length === 0) {
      return null;
    }

    const readyCount = marketData.filter(idx => idx.data_available !== false).length;
    const totalCount = marketData.length;

    return (
      <div key={marketName} className="bg-dark-800 rounded-lg p-4 border border-dark-700">
        <div className="flex items-center space-x-2 mb-4">
          <span className="text-2xl">{icon}</span>
          <h3 className="text-lg font-bold text-white">{marketName}</h3>
          <span className="text-sm text-dark-400 font-medium">({totalCount} indexes)</span>
          {showShortsUI && readyCount < totalCount && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30">
              {readyCount} of {totalCount} ready
            </span>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b-2 border-dark-700">
                <th className="text-left py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Index</th>
                <th className="text-center py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Since</th>
                <th className="text-right py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Index</th>
                <th className={`text-right py-3 px-2 ${currentStrategy.color} font-semibold bg-dark-800 uppercase text-xs tracking-wider`}>{currentStrategy.label}</th>
                <th className="text-right py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Trades/Yr</th>
                <th className="text-right py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Avg Hold</th>
                <th className="text-right py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">% In Mkt</th>
                <th className="text-right py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Win Rate</th>
                <th className="text-right py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Avg Ret</th>
                <th className="text-center py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Gain/Loss</th>
                <th className="text-right py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Exp. Ratio</th>
                <th className="text-right py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Best</th>
                <th className="text-right py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">Worst</th>
                <th className="text-center py-3 px-2 text-dark-300 font-semibold bg-dark-800 uppercase text-xs tracking-wider">MAE</th>
              </tr>
            </thead>
            <tbody>
              {marketData.map((index, idx) => {
                if (index.data_available === false) {
                  return (
                    <tr key={idx} className="bg-dark-800/40 border-b border-dark-700/50 opacity-60">
                      <td className="py-2.5 px-2 font-medium">
                        <div className="flex items-center gap-2">
                          <span className="text-dark-200">{index.symbol_name}</span>
                          <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-dark-700 text-dark-300 border border-dark-600">
                            Not available
                          </span>
                        </div>
                      </td>
                      <td colSpan={13} className="py-2.5 px-2 text-center text-dark-500 text-sm italic">
                        {positionMode === 'rec_shorts'
                          ? 'Shorts not available for this index'
                          : 'No data for this index yet'}
                      </td>
                    </tr>
                  );
                }

                const strategyCagr = getStrategyCagr(index);
                const strategyOutperf = getStrategyOutperf(index);
                const isBeating = strategyOutperf && strategyOutperf.startsWith('+');
                const rowClass = isBeating
                  ? 'bg-green-900/10 border-b border-green-800/30'
                  : 'bg-red-900/10 border-b border-red-800/30';

                // Non-negotiable rules - parse values
                const outperfValue = parseFloat(String(strategyOutperf).replace(/[+%]/g, ''));
                const winRateValue = parseFloat(index.win_rate);
                const avgReturnValue = parseFloat(index.avg_return_per_trade);
                const avgGainValue = parseFloat(index.avg_gain);
                const avgLossValue = parseFloat(index.avg_loss);
                const gainLossRatio = avgGainValue && avgLossValue ? Math.abs(avgGainValue) / Math.abs(avgLossValue) : 0;
                // Expectancy Ratio = Expectancy / Avg Loss = AVG RET / Avg Loss
                const expectancyRatio = (avgReturnValue && avgLossValue)
                  ? avgReturnValue / Math.abs(avgLossValue)
                  : null;
                const worstLossValue = parseFloat(index.worst_loss);
                const avgMaeValue = parseFloat(index.avg_mae);
                const maxMaeValue = parseFloat(index.max_mae);
                const maeRatio = avgMaeValue && maxMaeValue ? maxMaeValue / avgMaeValue : 0;
                const timeInMarketPct = parseFloat(index.time_in_market_pct);
                const indexCagrRaw = index.index_cagr_raw;
                const strategyCagrRaw = index.marketvibes_cagr_raw;

                // Check violations
                // Rule 1: Time in market should be <= 2/3 (66.67%)
                const isHighTimeInMarket = !isNaN(timeInMarketPct) && timeInMarketPct > 66.67;
                // Rule 2: Strategy CAGR should be >= 50% above Index CAGR
                const isLowCagrOutperf = (indexCagrRaw !== null && strategyCagrRaw !== null && indexCagrRaw > 0)
                  ? strategyCagrRaw < (indexCagrRaw * 1.5)
                  : false;
                const isLowWinRate = !isNaN(winRateValue) && winRateValue < 50;
                const isLowAvgReturn = !isNaN(avgReturnValue) && avgReturnValue < 4.5;
                const isLowGainLossRatio = gainLossRatio > 0 && gainLossRatio < 2.75;
                // Rule 3: Expectancy ratio should be > 1
                const isLowExpectancy = expectancyRatio !== null && expectancyRatio < 1;
                const isHighLoss = !isNaN(worstLossValue) && worstLossValue > 10;
                const isHighMaeRatio = maeRatio > 3;

                // Shorts-only sub-row appears below the main row only on
                // rec_shorts mode AND only for indexes that actually have
                // short trades in the file. Pure presentation layer — the
                // backend already computed the breakdown.
                const showShortsSubRow =
                  positionMode === 'rec_shorts' &&
                  index.data_available !== false &&
                  index.short_trades && index.short_trades > 0;
                const subShortExpRatio = index.short_exp_ratio;

                return (
                  <React.Fragment key={idx}>
                  <tr className={`${rowClass} hover:bg-dark-700/30`}>
                    <td className="py-2.5 px-2 text-white font-medium">
                      {(() => {
                        // ⓘ next to the index name when After Costs is on AND
                        // this symbol is configured. Click opens a popover
                        // with the cost recipe for THIS row's avg-days hold.
                        const code = (index.symbol_code || '').toUpperCase();
                        const COST_INPUTS = {
                          NIFTY50:   { impact: 0.30, label: 'Nifty 50' },
                          NIFTYBANK: { impact: 0.40, label: 'Nifty Bank' },
                          SENSEX:    { impact: 0.30, label: 'Sensex' },
                        };
                        const cfg = COST_INPUTS[code];
                        const showInfo = data?.net_costs && cfg && index.marketvibes_cagr_net;
                        if (!showInfo) return index.symbol_name;
                        const days = index.avg_days_held || 60;
                        const impactCost = cfg.impact;
                        const carryCost = days / 30;
                        const interestCost = days / 100;
                        const totalCost = impactCost + carryCost + interestCost;
                        const isOpen = costInfoSymbol === code;
                        return (
                          <span className="relative inline-flex items-center gap-1.5" data-cost-popover>
                            <span>{index.symbol_name}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCostInfoSymbol(isOpen ? null : code);
                              }}
                              className={`select-none w-4 h-4 rounded-full border text-[10px] leading-none flex items-center justify-center transition-colors ${
                                isOpen
                                  ? 'bg-amber-500 text-dark-900 border-amber-400'
                                  : 'border-amber-400/60 text-amber-300/80 hover:bg-amber-500/20'
                              }`}
                              aria-label={`Cost recipe for ${cfg.label}`}
                              title="Click for cost breakdown"
                            >ⓘ</button>
                            {isOpen && (
                              <div
                                className="absolute left-0 top-full mt-2 w-[340px] z-30 text-left bg-dark-900 border border-amber-700/60 rounded-lg shadow-2xl p-4 normal-case font-normal"
                                data-cost-popover
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="flex items-center justify-between mb-3">
                                  <h5 className="text-sm font-bold text-amber-200">
                                    After Costs — {cfg.label}
                                  </h5>
                                  <button
                                    type="button"
                                    onClick={() => setCostInfoSymbol(null)}
                                    className="text-dark-400 hover:text-white text-xs"
                                    aria-label="Close"
                                  >✕</button>
                                </div>
                                <p className="text-[11px] text-dark-300 mb-3">
                                  We subtract three frictions from every completed
                                  trade's gross % gain. Numbers below use this
                                  row's <b>avg hold of {days} days</b>.
                                </p>
                                <table className="w-full text-[11px] mb-3">
                                  <tbody>
                                    <tr className="border-b border-dark-700/50">
                                      <td className="py-1.5 pr-2 text-dark-300">
                                        <div className="font-semibold text-white">Impact</div>
                                        <div className="text-[10px] text-dark-400">slippage on entry + exit (one-time)</div>
                                      </td>
                                      <td className="py-1.5 text-right text-amber-300 font-semibold">
                                        {impactCost.toFixed(2)}%
                                      </td>
                                    </tr>
                                    <tr className="border-b border-dark-700/50">
                                      <td className="py-1.5 pr-2 text-dark-300">
                                        <div className="font-semibold text-white">Carry</div>
                                        <div className="text-[10px] text-dark-400">days ÷ 30 — longs pay, shorts earn</div>
                                      </td>
                                      <td className="py-1.5 text-right text-amber-300 font-semibold">
                                        {carryCost.toFixed(2)}%
                                      </td>
                                    </tr>
                                    <tr>
                                      <td className="py-1.5 pr-2 text-dark-300">
                                        <div className="font-semibold text-white">Interest</div>
                                        <div className="text-[10px] text-dark-400">days ÷ 100 (18% rate × 20% margin / 360)</div>
                                      </td>
                                      <td className="py-1.5 text-right text-amber-300 font-semibold">
                                        {interestCost.toFixed(2)}%
                                      </td>
                                    </tr>
                                    <tr className="border-t-2 border-amber-700/50">
                                      <td className="py-1.5 pr-2 text-white font-bold">Total drag / trade</td>
                                      <td className="py-1.5 text-right text-amber-200 font-bold">
                                        {totalCost.toFixed(2)}%
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                                <div className="bg-dark-800/70 rounded p-2 text-[11px]">
                                  <div className="flex justify-between">
                                    <span className="text-dark-300">Gross MV CAGR</span>
                                    <span className="text-white font-semibold">{strategyCagr || '-'}</span>
                                  </div>
                                  <div className="flex justify-between mt-1">
                                    <span className="text-dark-300">After-Cost CAGR</span>
                                    <span className="text-amber-300 font-semibold">{index.marketvibes_cagr_net}</span>
                                  </div>
                                  <div className="flex justify-between mt-1">
                                    <span className="text-dark-300">vs Index CAGR</span>
                                    <span className={`font-semibold ${(index.outperformance_net || '').startsWith('-') ? 'text-red-400' : 'text-emerald-400'}`}>
                                      {index.outperformance_net || '-'}
                                    </span>
                                  </div>
                                </div>
                                <p className="text-[10px] text-dark-400 mt-3">
                                  Costs only apply to the three configured symbols
                                  (Nifty 50, Nifty Bank, Sensex). Others stay gross.
                                </p>
                              </div>
                            )}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="py-2.5 px-2 text-center text-dark-300">{index.since_year}</td>
                    <td className="py-2.5 px-2 text-right text-blue-400 font-semibold">
                      {index.index_cagr || 'N/A'}
                    </td>
                    <td className={`py-2.5 px-2 text-right font-semibold ${isLowCagrOutperf ? 'bg-red-900/40' : ''} ${
                      isBeating ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {/* When After Costs is on AND this symbol is configured,
                          render the cost-adjusted value directly. The ⓘ for
                          drilling into the cost recipe lives next to the
                          INDEX name on the left of the row. */}
                      {(data?.net_costs && index.marketvibes_cagr_net) || strategyCagr || '-'}
                    </td>
                    <td className="py-2.5 px-2 text-right text-dark-200">
                      {index.trades_per_year || 'N/A'}
                    </td>
                    <td className="py-2.5 px-2 text-right text-dark-200">
                      {index.avg_days_held ? `${index.avg_days_held}d` : 'N/A'}
                    </td>
                    <td className={`py-2.5 px-2 text-right ${isHighTimeInMarket ? 'bg-red-900/40' : ''}`}>
                      <span className={isHighTimeInMarket ? 'text-red-400 font-semibold' : 'text-dark-200'}>
                        {index.time_in_market_pct ? `${index.time_in_market_pct}%` : 'N/A'}
                      </span>
                    </td>
                    <td className={`py-2.5 px-2 text-right ${isLowWinRate ? 'bg-red-900/40' : ''}`}>
                      <span className={`font-semibold ${isLowWinRate ? 'text-red-400' : 'text-yellow-400'}`}>
                        {index.win_rate || 'N/A'}
                      </span>
                    </td>
                    <td className={`py-2.5 px-2 text-right ${isLowAvgReturn ? 'bg-red-900/40' : ''}`}>
                      <span className={`font-semibold ${isLowAvgReturn ? 'text-red-400' : 'text-primary-400'}`}>
                        {index.avg_return_per_trade || 'N/A'}
                      </span>
                    </td>
                    <td className={`py-2.5 px-2 text-center ${isLowGainLossRatio ? 'bg-red-900/40' : ''}`}>
                      <div>
                        <span className="text-green-400">{index.avg_gain || 'N/A'}</span>
                        <span className="text-dark-500 mx-0.5">/</span>
                        <span className="text-red-400">{index.avg_loss || 'N/A'}</span>
                      </div>
                      {index.avg_gain && index.avg_loss && (
                        <div className={`text-xs font-semibold mt-0.5 ${isLowGainLossRatio ? 'text-red-400' : 'text-yellow-400'}`}>
                          {gainLossRatio.toFixed(2)}x
                        </div>
                      )}
                    </td>
                    <td className={`py-2.5 px-2 text-right ${isLowExpectancy ? 'bg-red-900/40' : ''}`}>
                      <span className={`font-semibold ${
                        expectancyRatio === null ? 'text-dark-500' :
                        expectancyRatio >= 1.5 ? 'text-green-400' :
                        expectancyRatio >= 1 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {expectancyRatio !== null ? expectancyRatio.toFixed(2) : 'N/A'}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-right text-emerald-400 font-semibold">
                      {index.best_gain || 'N/A'}
                    </td>
                    <td className={`py-2.5 px-2 text-right ${isHighLoss ? 'bg-red-900/40' : ''}`}>
                      <div className={`font-semibold ${isHighLoss ? 'text-red-400' : 'text-rose-400'}`}>
                        {index.worst_loss || 'N/A'}
                      </div>
                      {index.worst_loss_date && (
                        <div className="text-[10px] text-dark-400">{formatDate(index.worst_loss_date)}</div>
                      )}
                    </td>
                    <td className={`py-2.5 px-2 text-center ${isHighMaeRatio ? 'bg-red-900/40' : ''}`}>
                      {(index.avg_mae || index.max_mae) ? (
                        <>
                          <span className="text-orange-400">{index.avg_mae || 'N/A'}</span>
                          <span className="text-dark-500 mx-0.5">/</span>
                          <span className="text-rose-400">{index.max_mae || 'N/A'}</span>
                          {index.avg_mae && index.max_mae && (
                            <div className={`text-xs font-semibold mt-0.5 ${isHighMaeRatio ? 'text-red-400' : 'text-yellow-400'}`}>
                              {maeRatio.toFixed(1)}x
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-dark-500">N/A</span>
                      )}
                    </td>
                  </tr>
                  {showShortsSubRow && (
                    /* 14 cells, matches the 14 column headers. Empty values
                       still render an empty <td/> so column alignment never
                       drifts. CAGR/outperformance columns are intentionally
                       blank — they don't apply to a shorts-only slice. */
                    <tr className="bg-orange-500/5 border-b border-orange-500/20 text-xs">
                      {/* 1. Index */}
                      <td className="py-1.5 pl-6 pr-2">
                        <span className="inline-flex items-center gap-1 text-orange-300 font-semibold uppercase tracking-wide text-[11px]">
                          <span aria-hidden="true">↘</span> Shorts ({index.short_trades})
                        </span>
                      </td>
                      {/* 2. Since */}
                      <td />
                      {/* 3. Index CAGR */}
                      <td />
                      {/* 4. MV CAGR — N/A for shorts-only by design */}
                      <td />
                      {/* 5. Trades/Yr */}
                      <td className="py-1.5 px-2 text-right text-orange-200">
                        {index.short_trades_per_year != null ? index.short_trades_per_year : '—'}
                      </td>
                      {/* 6. Avg Hold */}
                      <td className="py-1.5 px-2 text-right text-orange-200">
                        {index.short_avg_days_held != null ? `${index.short_avg_days_held}d` : '—'}
                      </td>
                      {/* 7. % In Mkt */}
                      <td className="py-1.5 px-2 text-right text-orange-200">
                        {index.short_time_in_market_pct != null ? `${index.short_time_in_market_pct}%` : '—'}
                      </td>
                      {/* 8. Win Rate */}
                      <td className="py-1.5 px-2 text-right text-orange-200 font-semibold">
                        {index.short_wr || '—'}
                      </td>
                      {/* 9. Avg Ret */}
                      <td className="py-1.5 px-2 text-right text-orange-200 font-semibold">
                        {index.short_avg_return || '—'}
                      </td>
                      {/* 10. Gain/Loss */}
                      <td className="py-1.5 px-2 text-center text-orange-200">
                        {(index.short_avg_gain || index.short_avg_loss) ? (
                          <>
                            <span className="text-emerald-400/90">{index.short_avg_gain || '—'}</span>
                            <span className="text-dark-500 mx-0.5">/</span>
                            <span className="text-red-400/90">{index.short_avg_loss || '—'}</span>
                          </>
                        ) : '—'}
                      </td>
                      {/* 11. Exp. Ratio */}
                      <td className="py-1.5 px-2 text-right text-orange-200 font-semibold">
                        {subShortExpRatio != null ? subShortExpRatio.toFixed(2) : '—'}
                      </td>
                      {/* 12. Best */}
                      <td className="py-1.5 px-2 text-right text-emerald-400/90">
                        {index.short_best_gain || '—'}
                      </td>
                      {/* 13. Worst */}
                      <td className="py-1.5 px-2 text-right text-rose-400/90">
                        {index.short_worst_loss || '—'}
                      </td>
                      {/* 14. MAE */}
                      <td className="py-1.5 px-2 text-center text-orange-200">
                        {(index.short_avg_mae || index.short_max_mae) ? (
                          <>
                            <span className="text-orange-400/90">{index.short_avg_mae || '—'}</span>
                            <span className="text-dark-500 mx-0.5">/</span>
                            <span className="text-rose-400/90">{index.short_max_mae || '—'}</span>
                          </>
                        ) : '—'}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-800/50 rounded-lg">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-dark-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
          <div className="flex items-center space-x-3 bg-dark-800 px-4 py-3 rounded-lg border border-dark-600 shadow-lg">
            <RefreshCw className="w-5 h-5 text-primary-400 animate-spin" />
            <span className="text-sm text-white font-medium">Refreshing...</span>
          </div>
        </div>
      )}

      <div className={`space-y-5 transition-opacity duration-300 ${loading ? 'opacity-50' : 'opacity-100'}`}>
        {/* Header with Controls - Same layout as Individual Indexes */}
        <div className="flex items-center gap-6">
          {/* Market Filter */}
          <div>
            <label className="block text-xs font-semibold text-dark-400 uppercase tracking-wide mb-1.5">Market</label>
            <div className="flex items-center bg-dark-700 rounded-lg p-1">
              {marketOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => setMarketFilter(option.value)}
                  className={`px-3 py-1.5 rounded text-sm font-semibold transition-all flex items-center space-x-1.5 ${
                    marketFilter === option.value
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'text-dark-400 hover:text-white'
                  }`}
                >
                  <span>{option.icon}</span>
                  <span>{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Strategy toggle — shared component, gated to apalshah, sticky.
              Hidden for everyone else, so the page renders exactly as before for them. */}
          {canUseShorts && <div className="h-10 w-px bg-dark-600"></div>}
          <PositionModeToggle />

          {/* After Costs toggle — same sticky-state key as Individual Indexes */}
          <div className="h-10 w-px bg-dark-600"></div>
          <label
            className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg hover:bg-dark-700 transition-colors"
            title={
              'Apply realistic trading frictions (NIFTY50 0.30% impact, 0.40% NIFTYBANK; ' +
              'carry = days/30 % paid on longs, earned on shorts; interest = days/100 %).\n' +
              'Re-derives MV CAGR, Win Rate, Avg Gain/Loss per index. Index CAGR stays gross. ' +
              'Currently 1X-only; non-Indian symbols see no change.'
            }
          >
            <input
              type="checkbox"
              checked={netCosts}
              onChange={(e) => setNetCosts(e.target.checked)}
              className="sr-only peer"
            />
            <div className="relative w-9 h-5 rounded-full transition-colors bg-dark-600 peer-checked:bg-amber-500">
              <span
                className={`absolute top-[2px] h-[16px] w-[16px] rounded-full bg-white transition-transform shadow ${
                  netCosts ? 'left-[20px]' : 'left-[2px]'
                }`}
              />
            </div>
            <span className={`text-xs font-semibold uppercase tracking-wide ${netCosts ? 'text-amber-300' : 'text-dark-400'}`}>
              After Costs
            </span>
          </label>

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Action Buttons */}
          <button
            onClick={fetchAllIndexes}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="text-sm">{loading ? 'Loading...' : 'Refresh'}</span>
          </button>
          <button
            onClick={exportToExcel}
            disabled={!data || loading || exporting}
            className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-dark-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
          >
            {exporting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span className="text-sm">Exporting...</span>
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                <span className="text-sm">Export Excel</span>
              </>
            )}
          </button>
        </div>

        {/* Non-Negotiable Rules Legend */}
        <div className="bg-dark-800 border border-dark-600 rounded-lg px-4 py-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 border-r border-dark-600 pr-4">
              <div className="w-3 h-3 bg-red-500 rounded-full"></div>
              <span className="text-sm font-bold text-red-400 uppercase tracking-wide">Non-Negotiables</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1.5 bg-dark-700 border border-dark-500 rounded-lg text-sm">
                <span className="text-dark-300">CAGR</span> <span className="text-white font-semibold">&lt; 1.5x Index</span>
              </span>
              <span className="px-3 py-1.5 bg-dark-700 border border-dark-500 rounded-lg text-sm">
                <span className="text-dark-300">% In Mkt</span> <span className="text-white font-semibold">&gt; 2/3 yr</span>
              </span>
              <span className="px-3 py-1.5 bg-dark-700 border border-dark-500 rounded-lg text-sm">
                <span className="text-dark-300">Exp. Ratio</span> <span className="text-white font-semibold">&lt; 1.0</span>
              </span>
              <span className="px-3 py-1.5 bg-dark-700 border border-dark-500 rounded-lg text-sm">
                <span className="text-dark-300">Win Rate</span> <span className="text-white font-semibold">&lt; 50%</span>
              </span>
              <span className="px-3 py-1.5 bg-dark-700 border border-dark-500 rounded-lg text-sm">
                <span className="text-dark-300">Avg Return</span> <span className="text-white font-semibold">≤ 4.5%</span>
              </span>
              <span className="px-3 py-1.5 bg-dark-700 border border-dark-500 rounded-lg text-sm">
                <span className="text-dark-300">Gain/Loss</span> <span className="text-white font-semibold">≤ 2.75x</span>
              </span>
              <span className="px-3 py-1.5 bg-dark-700 border border-dark-500 rounded-lg text-sm">
                <span className="text-dark-300">Worst Loss</span> <span className="text-white font-semibold">≥ -10%</span>
              </span>
              <span className="px-3 py-1.5 bg-dark-700 border border-dark-500 rounded-lg text-sm">
                <span className="text-dark-300">MAE</span> <span className="text-white font-semibold">&gt; 3x</span>
              </span>
            </div>
          </div>
        </div>

        {/* Market Tables */}
        {data?.markets && (
          <div className="space-y-5">
            {canSeeMarket('india') && (marketFilter === 'all' || marketFilter === 'india') && renderMarketTable('India', filterMarketData(data.markets.india, 'india'), '🇮🇳')}
            {canSeeMarket('us') && (marketFilter === 'all' || marketFilter === 'us') && renderMarketTable('US', filterMarketData(data.markets.us, 'us'), '🇺🇸')}
            {canSeeMarket('crypto') && (marketFilter === 'all' || marketFilter === 'crypto') && renderMarketTable('Crypto', filterMarketData(data.markets.crypto, 'crypto'), '₿')}
            {canSeeMarket('asia_pacific') && (marketFilter === 'all' || marketFilter === 'asia_pacific') && renderMarketTable('Asia Pacific', filterMarketData(data.markets.asia_pacific, 'asia_pacific'), '🌏')}
            {canSeeMarket('europe') && (marketFilter === 'all' || marketFilter === 'europe') && renderMarketTable('Europe', filterMarketData(data.markets.europe, 'europe'), '🇪🇺')}
            {canSeeMarket('precious_metals') && (marketFilter === 'all' || marketFilter === 'precious_metals') && renderMarketTable('Precious Metals', filterMarketData(data.markets.precious_metals, 'precious_metals'), '🥇')}
          </div>
        )}

        {/* Empty State */}
        {!loading && (!data || data.total_indexes === 0) && (
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 text-dark-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Reports Available</h3>
            <p className="text-dark-400">No transaction files found for any indexes</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Period Performance Tab — compares strategy returns vs buy-and-hold index returns
// over rolling look-back periods (3M, 6M, 9M, 1Y, 2Y, 3Y, 4Y, 5Y)
const OutperformanceSummaryTab = () => {
  const { marketOptions: baseMarkets } = useAvailableMarkets();
  const allowedMarkets = getAllowedMarkets();

  const marketOptions = baseMarkets.length > 1
    ? [{ value: 'all', label: 'All Markets', icon: '🌍' }, ...baseMarkets]
    : baseMarkets;

  const defaultMarket = allowedMarkets.includes('all') ? 'all' : (baseMarkets[0]?.value || 'india');

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [marketFilter, setMarketFilter] = useStickyState('reports.period.marketFilter', defaultMarket);
  // Extended periods (10Y, 15Y, 20Y) hidden by default — long histories
  // mostly matter for comparison enthusiasts; default keeps the table compact.
  const [showExtendedPeriods, setShowExtendedPeriods] = useStickyState('reports.period.showExtended', false);
  const [exporting, setExporting] = useState(false);

  // Single shared positions toggle. Period Performance currently only has
  // long_only data plumbed through the backend; the with_shorts and rec_shorts
  // modes are flagged via positionMode but the values currently come back the
  // same until the outperformance endpoint is taught to read other folders.
  const { mode: periodPositionMode, canUseShorts: periodCanUseShorts } = usePositionMode();
  // Same sticky-state key as the other Reports surfaces.
  const [netCosts, setNetCosts] = useStickyState('reports.netCosts', false);
  // Existing render code reads `leverage` for class/label switches; keep it as
  // a constant set to '1x' so AES branches in the JSX simply never fire.
  const leverage = '1x';

  const canSeeMarket = (market) => {
    if (allowedMarkets.includes('all')) return true;
    return allowedMarkets.includes(market);
  };

  const filterMarketData = (marketData, market) => {
    if (!marketData) return [];
    return marketData.filter(index => isSymbolAllowed(market, index.symbol));
  };

  useEffect(() => {
    fetchOutperformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketFilter, periodPositionMode, netCosts]);

  const getReturnsForStrategy = (index, strategy) => {
    if (strategy === '1x') return index.returns_1x;
    if (strategy === 'aes_moderate') return index.returns_aes_moderate;
    if (strategy === 'aes_aggressive') return index.returns_aes_aggressive;
    return index.returns_2x;
  };

  const exportToExcel = () => {
    if (!data || !data.sectors) return;

    setExporting(true);
    try {
      const workbook = XLSX.utils.book_new();
      const periods = data.periods || ['3M', '6M', '9M', '1Y', '2Y', '3Y', '4Y', '5Y'];

      const createMarketSheet = (marketData) => {
        if (!marketData || marketData.length === 0) return null;
        const headers = [
          'Index',
          ...periods.flatMap(p => [`${p} Index %`, `${p} Strategy %`, `${p} Outperf %`]),
        ];
        const rows = marketData.map(index => {
          const returns = getReturnsForStrategy(index, leverage) || {};
          const cells = [index.symbol_name || index.symbol];
          periods.forEach(p => {
            const r = returns[p] || {};
            cells.push(r.index_return != null ? `${r.index_return.toFixed(2)}%` : 'N/A');
            cells.push(r.strategy_return != null ? `${r.strategy_return.toFixed(2)}%` : 'N/A');
            cells.push(r.outperf != null ? `${r.outperf.toFixed(2)}%` : 'N/A');
          });
          return cells;
        });
        return [headers, ...rows];
      };

      const allMarketsData = [
        { key: 'india', name: 'India' },
        { key: 'us', name: 'US' },
        { key: 'crypto', name: 'Crypto' },
        { key: 'asia_pacific', name: 'Asia Pacific' },
        { key: 'europe', name: 'Europe' },
        { key: 'precious_metals', name: 'Precious Metals' },
      ];
      const markets = marketFilter === 'all'
        ? allMarketsData.filter(m => canSeeMarket(m.key))
        : allMarketsData.filter(m => m.key === marketFilter && canSeeMarket(m.key));

      const isSingleMarket = marketFilter !== 'all';
      const leverageLabels = { '1x': '1X', 'aes_moderate': 'AES Mod', 'aes_aggressive': 'AES Agg' };

      markets.forEach(market => {
        const sheetData = createMarketSheet(data.sectors[market.key]);
        if (sheetData) {
          const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
          const sheetName = isSingleMarket ? leverageLabels[leverage] : `${market.name} ${leverageLabels[leverage]}`;
          XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
        }
      });

      const dateStr = new Date().toISOString().split('T')[0];
      const marketName = isSingleMarket ? `_${marketFilter}` : '';
      const filename = `period_performance${marketName}_${leverage}_${dateStr}.xlsx`;
      XLSX.writeFile(workbook, filename);
    } finally {
      setExporting(false);
    }
  };

  const fetchOutperformance = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        market: marketFilter,
        position_mode: periodPositionMode,
      });
      if (netCosts) params.set('net_costs', 'true');
      const response = await fetch(`${API_BASE_URL}/analytics/reports/outperformance/?${params}`, {
        cache: 'no-store',
        headers: token ? { 'Authorization': `Basic ${token}` } : {},
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const outperformanceData = await response.json();
      setData(outperformanceData);
    } catch (error) {
      console.error('Error loading period performance data:', error);
      setError(error.message || 'Failed to load period performance data');
    } finally {
      setLoading(false);
    }
  };

  const allPeriods = (data && data.periods) || ['3M', '6M', '9M', '1Y', '2Y', '3Y', '4Y', '5Y'];
  const extendedPeriods = (data && data.extended_periods) || [];
  // Hide extended period columns when the toggle is off.
  const periods = showExtendedPeriods
    ? allPeriods
    : allPeriods.filter(p => !extendedPeriods.includes(p));

  // Format a numeric return value with +/- sign and % suffix
  const formatReturn = (val) => {
    if (val == null || isNaN(val)) return '-';
    const sign = val >= 0 ? '+' : '';
    return `${sign}${val.toFixed(2)}%`;
  };

  // Cell color for MV strategy based on whether it beats the index
  const strategyCellColor = (outperf) => {
    if (outperf == null || isNaN(outperf)) return 'text-dark-300';
    if (outperf >= 10) return 'text-green-300 bg-green-900/30 font-semibold';
    if (outperf > 0) return 'text-emerald-300 bg-emerald-900/20';
    if (outperf > -10) return 'text-orange-300 bg-orange-900/20';
    return 'text-red-300 bg-red-900/30';
  };

  const renderMarketTable = (marketName, marketData, icon) => {
    if (!marketData || marketData.length === 0) {
      return null;
    }

    return (
      <div key={marketName} className="bg-dark-800 rounded-lg border border-dark-700 overflow-hidden">
        {/* Market Header */}
        <div className="bg-dark-700/50 px-4 py-2 border-b border-dark-600">
          <div className="flex items-center space-x-2">
            <span className="text-xl">{icon}</span>
            <h3 className="text-base font-bold text-white">{marketName}</h3>
            <span className="text-sm text-dark-400">({marketData.length} indexes)</span>
          </div>
        </div>

        {/* Table */}
        <div className="p-3">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-dark-700">
                  <th className="text-left py-2 px-2 text-dark-300 font-semibold uppercase tracking-wider sticky left-0 bg-dark-800" rowSpan={2}>Index</th>
                  {periods.map(period => (
                    <th key={period} className="text-center pt-2 pb-0.5 px-3 text-dark-300 font-semibold uppercase tracking-wider border-l border-dark-700">
                      {period}
                    </th>
                  ))}
                </tr>
                <tr className="border-b-2 border-dark-600">
                  {periods.map(period => (
                    <th key={period} className="text-center pt-0 pb-1.5 px-3 text-[10px] text-dark-500 font-normal normal-case tracking-normal border-l border-dark-700/50">
                      <span className="text-white">MV</span>
                      <span className="mx-1">/</span>
                      <span className="text-dark-400">Index</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {marketData.map((index, idx) => {
                  const returns = getReturnsForStrategy(index, leverage) || {};
                  return (
                    <tr key={idx} className="border-b border-dark-700/30 hover:bg-dark-700/20">
                      <td className="py-2 px-2 text-white font-medium sticky left-0 bg-dark-800">
                        <div className="flex flex-col">
                          <span>{index.symbol_name || index.symbol}</span>
                          <span className="text-[10px] text-dark-500">{index.symbol}</span>
                        </div>
                      </td>
                      {periods.map(period => {
                        const r = returns[period] || {};
                        const idx_r = r.index_return;
                        const strat_r = r.strategy_return;
                        const outp = r.outperf;
                        const cellColor = strategyCellColor(outp);
                        return (
                          <td
                            key={period}
                            className={`py-2 px-3 text-center border-l border-dark-700/50 tabular-nums whitespace-nowrap ${cellColor}`}
                            title={idx_r != null ? `MV Strategy: ${formatReturn(strat_r)} | Index B&H: ${formatReturn(idx_r)} | Outperf: ${formatReturn(outp)}` : ''}
                          >
                            <span className="font-semibold">{formatReturn(strat_r)}</span>
                            <span className="text-dark-400 mx-1">/</span>
                            <span className="text-dark-300 text-[11px]">{formatReturn(idx_r)}</span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="p-4 bg-red-900/20 border border-red-800/50 rounded-lg">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-dark-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center rounded-lg">
          <div className="flex items-center space-x-3 bg-dark-800 px-4 py-3 rounded-lg border border-dark-600 shadow-lg">
            <RefreshCw className="w-5 h-5 text-primary-400 animate-spin" />
            <span className="text-sm text-white font-medium">Loading...</span>
          </div>
        </div>
      )}

      <div className={`space-y-4 transition-opacity duration-300 ${loading ? 'opacity-50' : 'opacity-100'}`}>
        {/* Header with Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            {/* Market Filter */}
            <div>
              <label className="block text-xs font-semibold text-dark-400 uppercase tracking-wide mb-1.5">Market</label>
              <div className="flex items-center bg-dark-700 rounded-lg p-1">
                {marketOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setMarketFilter(option.value)}
                    className={`px-3 py-1.5 rounded text-sm font-semibold transition-all flex items-center space-x-1.5 ${
                      marketFilter === option.value
                        ? 'bg-teal-600 text-white shadow-sm'
                        : 'text-dark-400 hover:text-white'
                    }`}
                  >
                    <span>{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Strategy toggle — shared component, gated to apalshah, sticky.
                Hidden for everyone else (page renders as long-only by default). */}
            {periodCanUseShorts && <div className="h-10 w-px bg-dark-600"></div>}
            <PositionModeToggle />

            <div className="h-10 w-px bg-dark-600"></div>
            <label
              className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg hover:bg-dark-700 transition-colors"
              title="Apply impact + carry + interest costs to per-period strategy returns. Index returns stay gross."
            >
              <input
                type="checkbox"
                checked={netCosts}
                onChange={(e) => setNetCosts(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-9 h-5 rounded-full transition-colors bg-dark-600 peer-checked:bg-amber-500">
                <span
                  className={`absolute top-[2px] h-[16px] w-[16px] rounded-full bg-white transition-transform shadow ${
                    netCosts ? 'left-[20px]' : 'left-[2px]'
                  }`}
                />
              </div>
              <span className={`text-xs font-semibold uppercase tracking-wide ${netCosts ? 'text-amber-300' : 'text-dark-400'}`}>
                After Costs
              </span>
            </label>

            <div className="h-10 w-px bg-dark-600"></div>
            <label
              className="flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg hover:bg-dark-700 transition-colors"
              title="Show 10Y / 15Y / 20Y rolling-period columns. Off by default to keep the table compact."
            >
              <input
                type="checkbox"
                checked={showExtendedPeriods}
                onChange={(e) => setShowExtendedPeriods(e.target.checked)}
                className="sr-only peer"
              />
              <div className="relative w-9 h-5 rounded-full transition-colors bg-dark-600 peer-checked:bg-blue-500">
                <span
                  className={`absolute top-[2px] h-[16px] w-[16px] rounded-full bg-white transition-transform shadow ${
                    showExtendedPeriods ? 'left-[20px]' : 'left-[2px]'
                  }`}
                />
              </div>
              <span className={`text-xs font-semibold uppercase tracking-wide ${showExtendedPeriods ? 'text-blue-300' : 'text-dark-400'}`}>
                10Y / 15Y / 20Y
              </span>
            </label>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchOutperformance}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm">Refresh</span>
            </button>
            <button
              onClick={exportToExcel}
              disabled={!data || loading || exporting}
              className="flex items-center space-x-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-dark-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
            >
              <Download className={`w-4 h-4 ${exporting ? 'animate-spin' : ''}`} />
              <span className="text-sm">{exporting ? 'Exporting...' : 'Export Excel'}</span>
            </button>
          </div>
        </div>

        {/* Title Banner */}
        <div className={`rounded-lg px-4 py-3 border ${
          leverage === 'aes_moderate' ? 'bg-purple-900/20 border-purple-700/50' :
          leverage === 'aes_aggressive' ? 'bg-rose-900/20 border-rose-700/50' :
          'bg-blue-900/20 border-blue-700/50'
        }`}>
          <div className="flex items-center space-x-2">
            <TrendingUp className={`w-5 h-5 ${
              leverage === 'aes_moderate' ? 'text-purple-400' :
              leverage === 'aes_aggressive' ? 'text-rose-400' :
              'text-blue-400'
            }`} />
            <span className={`font-semibold ${
              leverage === 'aes_moderate' ? 'text-purple-300' :
              leverage === 'aes_aggressive' ? 'text-rose-300' :
              'text-blue-300'
            }`}>
              Period Performance — MarketVibes {leverage === 'aes_moderate' ? 'AES Moderate' : leverage === 'aes_aggressive' ? 'AES Aggressive' : '1X'} vs Buy &amp; Hold
            </span>
            <span className="text-dark-400 text-sm ml-2">
              — {data?.total_indexes || 0} indexes · rolling returns
            </span>
            {data?.net_costs && (
              <span
                className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-900/40 text-amber-300 border border-amber-700/50"
                title="Strategy returns in this table are net of impact + carry + interest costs. Index returns stay gross."
              >
                After Costs
              </span>
            )}
          </div>
        </div>

        {/* Color Legend */}
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs">
          <span className="text-dark-400">Cell color (MV vs Index):</span>
          <span className="px-2 py-0.5 rounded bg-green-900/30 text-green-300 font-medium">Beats by ≥10%</span>
          <span className="px-2 py-0.5 rounded bg-emerald-900/20 text-emerald-300 font-medium">Beats</span>
          <span className="px-2 py-0.5 rounded bg-orange-900/20 text-orange-300 font-medium">Lags</span>
          <span className="px-2 py-0.5 rounded bg-red-900/30 text-red-300 font-medium">Lags by ≥10%</span>
        </div>

        {/* Market Tables */}
        {data?.sectors && (
          <div className="space-y-4">
            {(marketFilter === 'all' || marketFilter === 'india') && canSeeMarket('india') &&
              renderMarketTable('India', filterMarketData(data.sectors.india, 'india'), '🇮🇳')}
            {(marketFilter === 'all' || marketFilter === 'us') && canSeeMarket('us') &&
              renderMarketTable('US', filterMarketData(data.sectors.us, 'us'), '🇺🇸')}
            {(marketFilter === 'all' || marketFilter === 'crypto') && canSeeMarket('crypto') &&
              renderMarketTable('Crypto', filterMarketData(data.sectors.crypto, 'crypto'), '₿')}
            {(marketFilter === 'all' || marketFilter === 'asia_pacific') && canSeeMarket('asia_pacific') &&
              renderMarketTable('Asia Pacific', filterMarketData(data.sectors.asia_pacific, 'asia_pacific'), '🌏')}
            {(marketFilter === 'all' || marketFilter === 'europe') && canSeeMarket('europe') &&
              renderMarketTable('Europe', filterMarketData(data.sectors.europe, 'europe'), '🇪🇺')}
          </div>
        )}

        {/* Empty State */}
        {!loading && (!data || data.total_indexes === 0) && (
          <div className="text-center py-12">
            <Trophy className="w-16 h-16 text-dark-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">No Period Performance Data</h3>
            <p className="text-dark-400">No index data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;
